// Web CRM (Business plane) — AI Agent suite static-UI translations.
// Split out of translations.js only for file size; merged into the same flat per-language lookup at
// load time (see translations.js). Same rules: STATIC UI CHROME ONLY, English default + fallback,
// never translate dynamic/user/backend content (conversation text, metric values, counts, %,
// dates, business/customer names, err.detail/error.message, status enums used in logic).
// Interpolated {values} are dynamic. All keys prefixed `aa.*`. Shared common.*/role.*/nav.* keys
// (Save, Cancel, Close, Error, Edit, Delete, etc.) intentionally reuse translations.js and are NOT
// redefined here.
//
// Scope: components/Business/AIAgent/AIAgentDashboard.jsx + AutoResolution.jsx.
// Every language below carries the identical key set; missing keys would simply fall back to English.

const aiAgentTranslations = {
  en: {
    'aa.configTitle': 'AI Agent Configuration',
    'aa.configDesc': 'Configure AI behavior, thresholds, and operational parameters',
    'aa.confidenceThreshold': 'Confidence Threshold',
    'aa.maxResponseTime': 'Max Response Time (seconds)',
    'aa.saveConfiguration': 'Save Configuration',
    'aa.autoEscalation': 'Auto-escalation for complex issues',
    'aa.missingInfo': 'Missing Information',
    'aa.aiConfigUpdated': 'AI Configuration Updated',
    'aa.aiConfigSaved': 'AI Agent settings have been saved successfully',
    'aa.notAvailableYet': 'Not available yet',
    'aa.viewDetails': 'View Details',
    'aa.provideFeedback': 'Provide Feedback',
    'aa.import': 'Import',
    'aa.export': 'Export',
    'aa.active': 'Active',
    'aa.disabled': 'Disabled',
    'aa.dashboardTitle': 'AI Agent Dashboard',
    'aa.dashboardSubtitle': 'Intelligent first-line resolution system',
    'aa.managedBy': '| Managed by {name}',
    'aa.configure': 'Configure',
    'aa.confThreshold70': '70% - More responses, lower accuracy',
    'aa.confThreshold80': '80% - Balanced approach',
    'aa.confThreshold85': '85% - Recommended setting',
    'aa.confThreshold90': '90% - High accuracy, fewer responses',
    'aa.confThreshold95': '95% - Maximum accuracy',
    'aa.learningMode': 'Learning Mode',
    'aa.activeLearning': 'Active Learning',
    'aa.passiveLearning': 'Passive Learning',
    'aa.supervisedOnly': 'Supervised Only',
    'aa.features': 'Features',
    'aa.trainAI': 'Train AI',
    'aa.trainingSessionTitle': 'AI Training Session',
    'aa.trainingSessionDesc': 'Add new training scenarios to improve AI responses',
    'aa.customerScenario': 'Customer Scenario *',
    'aa.scenarioPlaceholder': 'Describe a customer issue or question...',
    'aa.expectedResponse': 'Expected AI Response *',
    'aa.expectedResponsePlaceholder': 'Ideal response the AI should provide...',
    'aa.difficultyLevel': 'Difficulty Level',
    'aa.easy': 'Easy',
    'aa.medium': 'Medium',
    'aa.hard': 'Hard',
    'aa.expert': 'Expert',
    'aa.category': 'Category',
    'aa.generalSupport': 'General Support',
    'aa.technicalIssues': 'Technical Issues',
    'aa.billingQuestions': 'Billing Questions',
    'aa.productInformation': 'Product Information',
    'aa.trainAIModel': 'Train AI Model',
    'aa.currentLoad': 'Current Load',
    'aa.activeAiConversations': 'active AI conversations',
    'aa.avgResponse': 'Avg Response',
    'aa.responseTime': 'response time',
    'aa.confidenceScore': 'Confidence Score',
    'aa.avgConfidence': 'avg confidence',
    'aa.queueLength': 'Queue Length',
    'aa.notAvailableYetLower': 'not available yet',
    'aa.tab.conversations': 'Active Conversations',
    'aa.tab.analytics': 'Performance Analytics',
    'aa.tab.knowledge': 'Knowledge Base',
    'aa.tab.learning': 'Learning & Feedback',
    'aa.realtimeConversations': 'Real-time Conversations ({count})',
    'aa.refreshConversations': 'Refresh conversations',
    'aa.loadingConversations': 'Loading conversations...',
    'aa.noActiveConversations': 'No active AI conversations',
    'aa.aiHandlesAutomatically': 'AI will handle new conversations automatically',
    'aa.customerInquiry': 'Customer Inquiry',
    'aa.processing': 'Processing...',
    'aa.justNow': 'Just now',
    'aa.convDetailUnavailable': 'Conversation detail view will be available in an upcoming release.',
    'aa.escalate': 'Escalate',
    'aa.escalationUnavailable': 'In-dashboard escalation will be available in an upcoming release.',
    'aa.aiStatus': 'AI Status',
    'aa.telemetryUnavailable': 'Live system telemetry is not available yet.',
    'aa.quickActions': 'Quick Actions',
    'aa.addKnowledge': 'Add Knowledge',
    'aa.viewAnalytics': 'View Analytics',
    'aa.analyticsTitle': 'AI Performance Analytics',
    'aa.analyticsDesc': 'Comprehensive AI agent performance metrics and insights',
    'aa.autoResolutionRate': 'Auto-Resolution Rate',
    'aa.conversations': 'Conversations',
    'aa.escalationRate': 'Escalation Rate',
    'aa.performanceBreakdown': 'Performance Breakdown',
    'aa.resolutionRate': 'Resolution Rate',
    'aa.customerSatisfaction': 'Customer Satisfaction',
    'aa.responseTimeLabel': 'Response Time',
    'aa.trainingLogs': 'Training Logs',
    'aa.trainingLogsTitle': 'AI Training Logs',
    'aa.trainingLogsDesc': 'Recent training sessions and model updates',
    'aa.noTrainingLogs': 'No training logs yet',
    'aa.trainingHistoryUpcoming': 'Training history will appear here in an upcoming release.',
    'aa.loadingAnalytics': 'Loading Analytics',
    'aa.loadingAnalyticsDesc': 'Generating comprehensive AI performance analytics...',
    'aa.loadingTrainingLogs': 'Loading Training Logs',
    'aa.loadingTrainingLogsDesc': 'Retrieving AI training history and performance logs...',
    'aa.loadingCaseDetails': 'Loading Case Details',
    'aa.loadingCaseDetailsDesc': 'Retrieving details for case #{id}',
    'aa.resolutionPerformance': 'Resolution Performance',
    'aa.learningProgress': 'Learning Progress',
    'aa.knowledgeBaseEntries': 'Knowledge Base Entries',
    'aa.successfulResolutions': 'Successful Resolutions',
    'aa.totalConversations': 'Total Conversations',
    'aa.knowledgeBaseEntriesCount': 'Knowledge Base Entries ({count})',
    'aa.addEntry': 'Add Entry',
    'aa.noKnowledgeEntries': 'No Knowledge Base Entries',
    'aa.addFirstEntry': 'Add your first knowledge base entry to start training the AI',
    'aa.createFirstEntry': 'Create First Entry',
    'aa.uses': '{count} uses',
    'aa.recentFeedback': 'Recent Feedback',
    'aa.noFeedback': 'No feedback yet',
    'aa.feedbackWillAppear': 'Customer feedback on AI resolutions will appear here.',
    'aa.learningOpportunities': 'Learning Opportunities',
    'aa.improveResolutionRate': 'Improve Resolution Rate',
    'aa.improveResolutionDesc': 'Review escalated conversations to find gaps you can close with new knowledge base entries.',
    'aa.reviewCases': 'Review Cases',
    'aa.patternDetection': 'Pattern Detection',
    'aa.patternDetectionDesc': 'Automated trend and pattern detection is not available yet.',
    'aa.updateTrainings': 'Update Trainings',
    'aa.addKnowledgeTitle': 'Add Knowledge Entry',
    'aa.addKnowledgeDesc': 'Add new information to the AI knowledge base',
    'aa.titleRequired': 'Title *',
    'aa.knowledgeTitlePlaceholder': 'Knowledge entry title',
    'aa.contentRequired': 'Content *',
    'aa.contentPlaceholder': 'Detailed content or solution',
    'aa.technical': 'Technical',
    'aa.billing': 'Billing',
    'aa.general': 'General',
    'aa.policy': 'Policy',
    'aa.priority': 'Priority',
    'aa.low': 'Low',
    'aa.high': 'High',
    'aa.tags': 'Tags',
    'aa.tagsPlaceholder': 'Tags separated by commas',
    'aa.addToKnowledgeBase': 'Add to Knowledge Base',
    'aa.importTitle': 'Import Knowledge Base',
    'aa.importDesc': 'Import knowledge entries from a CSV or JSON file',
    'aa.selectFile': 'Select File',
    'aa.supportedFormats': 'Supported formats: CSV, JSON. Maximum file size: 10MB',
    'aa.exportTitle': 'Export Knowledge Base',
    'aa.exportDesc': 'Choose the format to export your knowledge base',
    'aa.csvFormat': 'CSV Format',
    'aa.csvFormatDesc': 'Spreadsheet compatible',
    'aa.jsonFormat': 'JSON Format',
    'aa.jsonFormatDesc': 'Structured data',
    'aa.reviewEscalatedTitle': 'Review Escalated Cases',
    'aa.reviewEscalatedDesc': 'Review and analyze cases that were escalated from AI to human agents',
    'aa.recentEscalatedCases': 'Recent Escalated Cases',
    'aa.noEscalatedCases': 'No escalated cases to review yet',
    'aa.escalatedWillAppear': 'Escalated conversations will appear here in an upcoming release.',
    'aa.aiConfidenceLabel': 'AI Confidence:',
    'aa.escalationAnalysis': 'Escalation Analysis',
    'aa.escalationAnalysisUnavailable': 'Escalation analysis is not available yet.',
    'aa.generateAnalysisReport': 'Generate Analysis Report',
    'aa.analysisReportUnavailable': 'Analysis report generation will be available in an upcoming release.',
    'aa.caseDetailsTitle': 'Case Details - #{id}',
    'aa.caseDetailsDesc': 'Detailed information about the escalated case',
    'aa.caseId': 'Case ID',
    'aa.customer': 'Customer',
    'aa.unknownCustomer': 'Unknown Customer',
    'aa.escalated': 'Escalated',
    'aa.unknownTime': 'Unknown Time',
    'aa.issueDescription': 'Issue Description',
    'aa.noIssueDescription': 'No issue description available',
    'aa.aiConfidenceLevel': 'AI Confidence Level',
    'aa.escalationReason': 'Escalation Reason',
    'aa.escalationReasonText': 'AI confidence level was below threshold. Complex technical issue requiring human expertise.',
    'aa.provideFeedbackCaseTitle': 'Provide Feedback - Case #{id}',
    'aa.provideFeedbackDesc': 'Your feedback helps improve AI performance for similar cases',
    'aa.caseSummary': 'Case Summary',
    'aa.feedbackCustomer': 'Customer: {name}',
    'aa.feedbackIssue': 'Issue: {issue}',
    'aa.yourFeedback': 'Your Feedback *',
    'aa.feedbackPlaceholder': 'How could the AI have handled this case better? What additional training or knowledge would help?',
    'aa.feedbackGuidelines': 'Feedback Guidelines:',
    'aa.feedbackGuideline1': 'Describe what the AI missed or misunderstood',
    'aa.feedbackGuideline2': 'Suggest knowledge base improvements',
    'aa.feedbackGuideline3': 'Note any customer communication issues',
    'aa.submitFeedback': 'Submit Feedback',
    'aa.trainingFillScenario': 'Please fill in scenario and expected response',
    'aa.trainingUnavailable': 'AI model training will be available in an upcoming release.',
    'aa.fillTitleContent': 'Please fill in title and content',
    'aa.knowledgeAdded': 'Knowledge Added',
    'aa.knowledgeAddedDesc': 'New knowledge entry has been added to the knowledge base',
    'aa.addKnowledgeError': 'Failed to add knowledge base entry',
    'aa.importUnavailable': 'Knowledge base import will be available in an upcoming release.',
    'aa.nothingToExport': 'Nothing to export',
    'aa.nothingToExportDesc': 'There are no knowledge base entries to export yet.',
    'aa.exportComplete': 'Export Complete',
    'aa.exportCompleteDesc': 'Knowledge base exported as {format}.',
    'aa.trainingUpdatesUnavailable': 'AI training updates will be available in an upcoming release.',
    'aa.feedbackRequired': 'Feedback Required',
    'aa.feedbackRequiredDesc': 'Please provide feedback before submitting.',
    'aa.feedbackSubmitted': 'Feedback Submitted',
    'aa.feedbackSubmittedDesc': 'Feedback for case #{id} has been recorded and will help improve AI performance.',
    'aa.saveConfigError': 'Failed to save AI configuration',
    'aa.loadingRules': 'Loading auto-resolution rules...',
    'aa.autoResolutionMgmt': 'Auto-Resolution Management',
    'aa.autoResolutionSubtitle': 'Configure intelligent response rules and thresholds',
    'aa.activeRulesBadge': '{count} Active Rules',
    'aa.aiSettings': 'AI Settings',
    'aa.aiModel': 'AI Model',
    'aa.advancedReasoning': 'Advanced reasoning and intelligent customer support',
    'aa.confidenceThresholdHint': 'Higher values = more careful responses (0.1-1.0)',
    'aa.advancedFeatures': 'Advanced Features',
    'aa.activeLearningFromInteractions': 'Active learning from customer interactions',
    'aa.activeRules': 'Active Rules',
    'aa.avgSuccessRate': 'Avg Success Rate',
    'aa.totalUsage': 'Total Usage',
    'aa.advancedAi': 'Advanced AI',
    'aa.tab.rules': 'Resolution Rules',
    'aa.tab.create': 'Create Rule',
    'aa.autoResolutionRulesCount': 'Auto-Resolution Rules ({count})',
    'aa.keywordsLabel': 'Keywords: {keywords}',
    'aa.confidenceLabel': 'Confidence: {value}%',
    'aa.usedLabel': 'Used: {count} times',
    'aa.successLabel': 'Success: {value}%',
    'aa.createNewRule': 'Create New Auto-Resolution Rule',
    'aa.ruleName': 'Rule Name *',
    'aa.ruleNamePlaceholder': 'Enter rule name',
    'aa.description': 'Description',
    'aa.descriptionPlaceholder': 'Describe what this rule handles',
    'aa.keywordsCommaSeparated': 'Keywords (comma-separated)',
    'aa.keywordsPlaceholder': 'password, reset, forgot, login',
    'aa.autoResponse': 'Auto-Response *',
    'aa.autoResponsePlaceholder': 'Enter the automatic response for this rule',
    'aa.enableRuleImmediately': 'Enable this rule immediately',
    'aa.saveRule': 'Save Rule',
    'aa.responseLabel': 'Response:',
    'aa.aiConfigSettings': 'AI Configuration Settings',
    'aa.aiEngine': 'AI Engine',
    'aa.poweredBy': 'Powered by {provider} ({model})',
    'aa.resolutionAccuracy': 'Resolution Accuracy',
    'aa.avgResponseTime': 'Avg Response Time',
    'aa.configureAiParams': 'Configure AI Parameters',
    'aa.editRuleTitle': 'Edit Auto-Resolution Rule',
    'aa.editRuleDesc': 'Modify the auto-resolution rule settings',
    'aa.ruleIsEnabled': 'Rule is enabled',
    'aa.updateRule': 'Update Rule',
    'aa.areYouSure': 'Are you sure?',
    'aa.deleteConfirmPre': 'This action cannot be undone. This will permanently delete the auto-resolution rule',
    'aa.deleteConfirmPost': 'and remove all associated data.',
    'aa.deleteRule': 'Delete Rule',
    'aa.fillRuleNameResponse': 'Please fill in the rule name and response',
    'aa.ruleCreated': 'Rule Created',
    'aa.ruleCreatedDesc': 'Auto-resolution rule has been created successfully',
    'aa.createRuleError': 'Failed to create auto-resolution rule',
    'aa.ruleUpdated': 'Rule Updated',
    'aa.ruleUpdatedDesc': 'Auto-resolution rule has been updated successfully',
    'aa.updateRuleError': 'Failed to update auto-resolution rule',
    'aa.ruleEnabledSuccess': 'Rule enabled successfully',
    'aa.ruleDisabledSuccess': 'Rule disabled successfully',
    'aa.ruleDeleted': 'Rule Deleted',
    'aa.ruleDeletedDesc': 'Auto-resolution rule has been deleted successfully',
    'aa.deleteRuleError': 'Failed to delete auto-resolution rule',
    'aa.loadRulesError': 'Failed to load auto-resolution rules',
    'aa.updateConfigError': 'Failed to update AI configuration',
  },
  es: {
    'aa.configTitle': 'Configuración del agente de IA',
    'aa.configDesc': 'Configure el comportamiento, los umbrales y los parámetros operativos de la IA',
    'aa.confidenceThreshold': 'Umbral de confianza',
    'aa.maxResponseTime': 'Tiempo máximo de respuesta (segundos)',
    'aa.saveConfiguration': 'Guardar configuración',
    'aa.autoEscalation': 'Escalado automático para problemas complejos',
    'aa.missingInfo': 'Información incompleta',
    'aa.aiConfigUpdated': 'Configuración de IA actualizada',
    'aa.aiConfigSaved': 'La configuración del agente de IA se ha guardado correctamente',
    'aa.notAvailableYet': 'Aún no disponible',
    'aa.viewDetails': 'Ver detalles',
    'aa.provideFeedback': 'Enviar comentarios',
    'aa.import': 'Importar',
    'aa.export': 'Exportar',
    'aa.active': 'Activo',
    'aa.disabled': 'Deshabilitado',
    'aa.dashboardTitle': 'Panel del agente de IA',
    'aa.dashboardSubtitle': 'Sistema inteligente de resolución de primera línea',
    'aa.managedBy': '| Gestionado por {name}',
    'aa.configure': 'Configurar',
    'aa.confThreshold70': '70 % - Más respuestas, menor precisión',
    'aa.confThreshold80': '80 % - Enfoque equilibrado',
    'aa.confThreshold85': '85 % - Configuración recomendada',
    'aa.confThreshold90': '90 % - Alta precisión, menos respuestas',
    'aa.confThreshold95': '95 % - Precisión máxima',
    'aa.learningMode': 'Modo de aprendizaje',
    'aa.activeLearning': 'Aprendizaje activo',
    'aa.passiveLearning': 'Aprendizaje pasivo',
    'aa.supervisedOnly': 'Solo supervisado',
    'aa.features': 'Funciones',
    'aa.trainAI': 'Entrenar IA',
    'aa.trainingSessionTitle': 'Sesión de entrenamiento de IA',
    'aa.trainingSessionDesc': 'Agregue nuevos escenarios de entrenamiento para mejorar las respuestas de la IA',
    'aa.customerScenario': 'Escenario del cliente *',
    'aa.scenarioPlaceholder': 'Describa un problema o una pregunta del cliente...',
    'aa.expectedResponse': 'Respuesta esperada de la IA *',
    'aa.expectedResponsePlaceholder': 'Respuesta ideal que debería dar la IA...',
    'aa.difficultyLevel': 'Nivel de dificultad',
    'aa.easy': 'Fácil',
    'aa.medium': 'Medio',
    'aa.hard': 'Difícil',
    'aa.expert': 'Experto',
    'aa.category': 'Categoría',
    'aa.generalSupport': 'Soporte general',
    'aa.technicalIssues': 'Problemas técnicos',
    'aa.billingQuestions': 'Preguntas de facturación',
    'aa.productInformation': 'Información del producto',
    'aa.trainAIModel': 'Entrenar modelo de IA',
    'aa.currentLoad': 'Carga actual',
    'aa.activeAiConversations': 'conversaciones de IA activas',
    'aa.avgResponse': 'Respuesta media',
    'aa.responseTime': 'tiempo de respuesta',
    'aa.confidenceScore': 'Puntuación de confianza',
    'aa.avgConfidence': 'confianza media',
    'aa.queueLength': 'Longitud de la cola',
    'aa.notAvailableYetLower': 'aún no disponible',
    'aa.tab.conversations': 'Conversaciones activas',
    'aa.tab.analytics': 'Análisis de rendimiento',
    'aa.tab.knowledge': 'Base de conocimiento',
    'aa.tab.learning': 'Aprendizaje y comentarios',
    'aa.realtimeConversations': 'Conversaciones en tiempo real ({count})',
    'aa.refreshConversations': 'Actualizar conversaciones',
    'aa.loadingConversations': 'Cargando conversaciones...',
    'aa.noActiveConversations': 'No hay conversaciones de IA activas',
    'aa.aiHandlesAutomatically': 'La IA gestionará las nuevas conversaciones automáticamente',
    'aa.customerInquiry': 'Consulta del cliente',
    'aa.processing': 'Procesando...',
    'aa.justNow': 'Ahora mismo',
    'aa.convDetailUnavailable': 'La vista de detalles de la conversación estará disponible en una próxima versión.',
    'aa.escalate': 'Escalar',
    'aa.escalationUnavailable': 'El escalado desde el panel estará disponible en una próxima versión.',
    'aa.aiStatus': 'Estado de la IA',
    'aa.telemetryUnavailable': 'La telemetría del sistema en vivo aún no está disponible.',
    'aa.quickActions': 'Acciones rápidas',
    'aa.addKnowledge': 'Agregar conocimiento',
    'aa.viewAnalytics': 'Ver análisis',
    'aa.analyticsTitle': 'Análisis de rendimiento de IA',
    'aa.analyticsDesc': 'Métricas e información completas del rendimiento del agente de IA',
    'aa.autoResolutionRate': 'Tasa de resolución automática',
    'aa.conversations': 'Conversaciones',
    'aa.escalationRate': 'Tasa de escalado',
    'aa.performanceBreakdown': 'Desglose del rendimiento',
    'aa.resolutionRate': 'Tasa de resolución',
    'aa.customerSatisfaction': 'Satisfacción del cliente',
    'aa.responseTimeLabel': 'Tiempo de respuesta',
    'aa.trainingLogs': 'Registros de entrenamiento',
    'aa.trainingLogsTitle': 'Registros de entrenamiento de IA',
    'aa.trainingLogsDesc': 'Sesiones de entrenamiento recientes y actualizaciones del modelo',
    'aa.noTrainingLogs': 'Aún no hay registros de entrenamiento',
    'aa.trainingHistoryUpcoming': 'El historial de entrenamiento aparecerá aquí en una próxima versión.',
    'aa.loadingAnalytics': 'Cargando análisis',
    'aa.loadingAnalyticsDesc': 'Generando análisis completos del rendimiento de la IA...',
    'aa.loadingTrainingLogs': 'Cargando registros de entrenamiento',
    'aa.loadingTrainingLogsDesc': 'Recuperando el historial de entrenamiento y los registros de rendimiento de la IA...',
    'aa.loadingCaseDetails': 'Cargando detalles del caso',
    'aa.loadingCaseDetailsDesc': 'Recuperando los detalles del caso n.º {id}',
    'aa.resolutionPerformance': 'Rendimiento de resolución',
    'aa.learningProgress': 'Progreso del aprendizaje',
    'aa.knowledgeBaseEntries': 'Entradas de la base de conocimiento',
    'aa.successfulResolutions': 'Resoluciones exitosas',
    'aa.totalConversations': 'Conversaciones totales',
    'aa.knowledgeBaseEntriesCount': 'Entradas de la base de conocimiento ({count})',
    'aa.addEntry': 'Agregar entrada',
    'aa.noKnowledgeEntries': 'No hay entradas en la base de conocimiento',
    'aa.addFirstEntry': 'Agregue su primera entrada a la base de conocimiento para empezar a entrenar la IA',
    'aa.createFirstEntry': 'Crear primera entrada',
    'aa.uses': '{count} usos',
    'aa.recentFeedback': 'Comentarios recientes',
    'aa.noFeedback': 'Aún no hay comentarios',
    'aa.feedbackWillAppear': 'Los comentarios de los clientes sobre las resoluciones de IA aparecerán aquí.',
    'aa.learningOpportunities': 'Oportunidades de aprendizaje',
    'aa.improveResolutionRate': 'Mejorar la tasa de resolución',
    'aa.improveResolutionDesc': 'Revise las conversaciones escaladas para encontrar lagunas que pueda cubrir con nuevas entradas en la base de conocimiento.',
    'aa.reviewCases': 'Revisar casos',
    'aa.patternDetection': 'Detección de patrones',
    'aa.patternDetectionDesc': 'La detección automática de tendencias y patrones aún no está disponible.',
    'aa.updateTrainings': 'Actualizar entrenamientos',
    'aa.addKnowledgeTitle': 'Agregar entrada de conocimiento',
    'aa.addKnowledgeDesc': 'Agregue nueva información a la base de conocimiento de la IA',
    'aa.titleRequired': 'Título *',
    'aa.knowledgeTitlePlaceholder': 'Título de la entrada de conocimiento',
    'aa.contentRequired': 'Contenido *',
    'aa.contentPlaceholder': 'Contenido detallado o solución',
    'aa.technical': 'Técnico',
    'aa.billing': 'Facturación',
    'aa.general': 'General',
    'aa.policy': 'Política',
    'aa.priority': 'Prioridad',
    'aa.low': 'Baja',
    'aa.high': 'Alta',
    'aa.tags': 'Etiquetas',
    'aa.tagsPlaceholder': 'Etiquetas separadas por comas',
    'aa.addToKnowledgeBase': 'Agregar a la base de conocimiento',
    'aa.importTitle': 'Importar base de conocimiento',
    'aa.importDesc': 'Importe entradas de conocimiento desde un archivo CSV o JSON',
    'aa.selectFile': 'Seleccionar archivo',
    'aa.supportedFormats': 'Formatos admitidos: CSV, JSON. Tamaño máximo de archivo: 10 MB',
    'aa.exportTitle': 'Exportar base de conocimiento',
    'aa.exportDesc': 'Elija el formato para exportar su base de conocimiento',
    'aa.csvFormat': 'Formato CSV',
    'aa.csvFormatDesc': 'Compatible con hojas de cálculo',
    'aa.jsonFormat': 'Formato JSON',
    'aa.jsonFormatDesc': 'Datos estructurados',
    'aa.reviewEscalatedTitle': 'Revisar casos escalados',
    'aa.reviewEscalatedDesc': 'Revise y analice los casos que se escalaron de la IA a agentes humanos',
    'aa.recentEscalatedCases': 'Casos escalados recientes',
    'aa.noEscalatedCases': 'Aún no hay casos escalados para revisar',
    'aa.escalatedWillAppear': 'Las conversaciones escaladas aparecerán aquí en una próxima versión.',
    'aa.aiConfidenceLabel': 'Confianza de la IA:',
    'aa.escalationAnalysis': 'Análisis de escalado',
    'aa.escalationAnalysisUnavailable': 'El análisis de escalado aún no está disponible.',
    'aa.generateAnalysisReport': 'Generar informe de análisis',
    'aa.analysisReportUnavailable': 'La generación de informes de análisis estará disponible en una próxima versión.',
    'aa.caseDetailsTitle': 'Detalles del caso - n.º {id}',
    'aa.caseDetailsDesc': 'Información detallada sobre el caso escalado',
    'aa.caseId': 'ID del caso',
    'aa.customer': 'Cliente',
    'aa.unknownCustomer': 'Cliente desconocido',
    'aa.escalated': 'Escalado',
    'aa.unknownTime': 'Hora desconocida',
    'aa.issueDescription': 'Descripción del problema',
    'aa.noIssueDescription': 'No hay descripción del problema disponible',
    'aa.aiConfidenceLevel': 'Nivel de confianza de la IA',
    'aa.escalationReason': 'Motivo del escalado',
    'aa.escalationReasonText': 'El nivel de confianza de la IA estaba por debajo del umbral. Problema técnico complejo que requiere experiencia humana.',
    'aa.provideFeedbackCaseTitle': 'Enviar comentarios - Caso n.º {id}',
    'aa.provideFeedbackDesc': 'Sus comentarios ayudan a mejorar el rendimiento de la IA en casos similares',
    'aa.caseSummary': 'Resumen del caso',
    'aa.feedbackCustomer': 'Cliente: {name}',
    'aa.feedbackIssue': 'Problema: {issue}',
    'aa.yourFeedback': 'Sus comentarios *',
    'aa.feedbackPlaceholder': '¿Cómo podría la IA haber gestionado mejor este caso? ¿Qué entrenamiento o conocimiento adicional ayudaría?',
    'aa.feedbackGuidelines': 'Pautas para los comentarios:',
    'aa.feedbackGuideline1': 'Describa lo que la IA pasó por alto o malinterpretó',
    'aa.feedbackGuideline2': 'Sugiera mejoras para la base de conocimiento',
    'aa.feedbackGuideline3': 'Anote cualquier problema de comunicación con el cliente',
    'aa.submitFeedback': 'Enviar comentarios',
    'aa.trainingFillScenario': 'Complete el escenario y la respuesta esperada',
    'aa.trainingUnavailable': 'El entrenamiento del modelo de IA estará disponible en una próxima versión.',
    'aa.fillTitleContent': 'Complete el título y el contenido',
    'aa.knowledgeAdded': 'Conocimiento agregado',
    'aa.knowledgeAddedDesc': 'Se ha agregado una nueva entrada a la base de conocimiento',
    'aa.addKnowledgeError': 'No se pudo agregar la entrada a la base de conocimiento',
    'aa.importUnavailable': 'La importación de la base de conocimiento estará disponible en una próxima versión.',
    'aa.nothingToExport': 'Nada que exportar',
    'aa.nothingToExportDesc': 'Aún no hay entradas en la base de conocimiento para exportar.',
    'aa.exportComplete': 'Exportación completada',
    'aa.exportCompleteDesc': 'Base de conocimiento exportada como {format}.',
    'aa.trainingUpdatesUnavailable': 'Las actualizaciones de entrenamiento de IA estarán disponibles en una próxima versión.',
    'aa.feedbackRequired': 'Se requieren comentarios',
    'aa.feedbackRequiredDesc': 'Proporcione comentarios antes de enviar.',
    'aa.feedbackSubmitted': 'Comentarios enviados',
    'aa.feedbackSubmittedDesc': 'Los comentarios del caso n.º {id} se han registrado y ayudarán a mejorar el rendimiento de la IA.',
    'aa.saveConfigError': 'No se pudo guardar la configuración de IA',
    'aa.loadingRules': 'Cargando reglas de resolución automática...',
    'aa.autoResolutionMgmt': 'Gestión de resolución automática',
    'aa.autoResolutionSubtitle': 'Configure reglas de respuesta inteligentes y umbrales',
    'aa.activeRulesBadge': '{count} reglas activas',
    'aa.aiSettings': 'Ajustes de IA',
    'aa.aiModel': 'Modelo de IA',
    'aa.advancedReasoning': 'Razonamiento avanzado y soporte al cliente inteligente',
    'aa.confidenceThresholdHint': 'Valores más altos = respuestas más cuidadosas (0.1-1.0)',
    'aa.advancedFeatures': 'Funciones avanzadas',
    'aa.activeLearningFromInteractions': 'Aprendizaje activo a partir de las interacciones con los clientes',
    'aa.activeRules': 'Reglas activas',
    'aa.avgSuccessRate': 'Tasa de éxito media',
    'aa.totalUsage': 'Uso total',
    'aa.advancedAi': 'IA avanzada',
    'aa.tab.rules': 'Reglas de resolución',
    'aa.tab.create': 'Crear regla',
    'aa.autoResolutionRulesCount': 'Reglas de resolución automática ({count})',
    'aa.keywordsLabel': 'Palabras clave: {keywords}',
    'aa.confidenceLabel': 'Confianza: {value} %',
    'aa.usedLabel': 'Usada: {count} veces',
    'aa.successLabel': 'Éxito: {value} %',
    'aa.createNewRule': 'Crear nueva regla de resolución automática',
    'aa.ruleName': 'Nombre de la regla *',
    'aa.ruleNamePlaceholder': 'Escriba el nombre de la regla',
    'aa.description': 'Descripción',
    'aa.descriptionPlaceholder': 'Describa lo que gestiona esta regla',
    'aa.keywordsCommaSeparated': 'Palabras clave (separadas por comas)',
    'aa.keywordsPlaceholder': 'contraseña, restablecer, olvidé, iniciar sesión',
    'aa.autoResponse': 'Respuesta automática *',
    'aa.autoResponsePlaceholder': 'Escriba la respuesta automática para esta regla',
    'aa.enableRuleImmediately': 'Habilitar esta regla de inmediato',
    'aa.saveRule': 'Guardar regla',
    'aa.responseLabel': 'Respuesta:',
    'aa.aiConfigSettings': 'Ajustes de configuración de IA',
    'aa.aiEngine': 'Motor de IA',
    'aa.poweredBy': 'Con tecnología de {provider} ({model})',
    'aa.resolutionAccuracy': 'Precisión de resolución',
    'aa.avgResponseTime': 'Tiempo medio de respuesta',
    'aa.configureAiParams': 'Configurar parámetros de IA',
    'aa.editRuleTitle': 'Editar regla de resolución automática',
    'aa.editRuleDesc': 'Modifique los ajustes de la regla de resolución automática',
    'aa.ruleIsEnabled': 'La regla está habilitada',
    'aa.updateRule': 'Actualizar regla',
    'aa.areYouSure': '¿Está seguro?',
    'aa.deleteConfirmPre': 'Esta acción no se puede deshacer. Esto eliminará permanentemente la regla de resolución automática',
    'aa.deleteConfirmPost': 'y eliminará todos los datos asociados.',
    'aa.deleteRule': 'Eliminar regla',
    'aa.fillRuleNameResponse': 'Complete el nombre de la regla y la respuesta',
    'aa.ruleCreated': 'Regla creada',
    'aa.ruleCreatedDesc': 'La regla de resolución automática se ha creado correctamente',
    'aa.createRuleError': 'No se pudo crear la regla de resolución automática',
    'aa.ruleUpdated': 'Regla actualizada',
    'aa.ruleUpdatedDesc': 'La regla de resolución automática se ha actualizado correctamente',
    'aa.updateRuleError': 'No se pudo actualizar la regla de resolución automática',
    'aa.ruleEnabledSuccess': 'Regla habilitada correctamente',
    'aa.ruleDisabledSuccess': 'Regla deshabilitada correctamente',
    'aa.ruleDeleted': 'Regla eliminada',
    'aa.ruleDeletedDesc': 'La regla de resolución automática se ha eliminado correctamente',
    'aa.deleteRuleError': 'No se pudo eliminar la regla de resolución automática',
    'aa.loadRulesError': 'No se pudieron cargar las reglas de resolución automática',
    'aa.updateConfigError': 'No se pudo actualizar la configuración de IA',
  },
  fr: {
    'aa.configTitle': "Configuration de l'agent IA",
    'aa.configDesc': "Configurez le comportement, les seuils et les paramètres opérationnels de l'IA",
    'aa.confidenceThreshold': 'Seuil de confiance',
    'aa.maxResponseTime': 'Temps de réponse maximal (secondes)',
    'aa.saveConfiguration': 'Enregistrer la configuration',
    'aa.autoEscalation': 'Escalade automatique pour les problèmes complexes',
    'aa.missingInfo': 'Informations manquantes',
    'aa.aiConfigUpdated': 'Configuration IA mise à jour',
    'aa.aiConfigSaved': "Les paramètres de l'agent IA ont été enregistrés avec succès",
    'aa.notAvailableYet': 'Pas encore disponible',
    'aa.viewDetails': 'Voir les détails',
    'aa.provideFeedback': 'Donner un avis',
    'aa.import': 'Importer',
    'aa.export': 'Exporter',
    'aa.active': 'Actif',
    'aa.disabled': 'Désactivé',
    'aa.dashboardTitle': "Tableau de bord de l'agent IA",
    'aa.dashboardSubtitle': 'Système intelligent de résolution de première ligne',
    'aa.managedBy': '| Géré par {name}',
    'aa.configure': 'Configurer',
    'aa.confThreshold70': '70 % - Plus de réponses, précision moindre',
    'aa.confThreshold80': '80 % - Approche équilibrée',
    'aa.confThreshold85': '85 % - Paramètre recommandé',
    'aa.confThreshold90': '90 % - Haute précision, moins de réponses',
    'aa.confThreshold95': '95 % - Précision maximale',
    'aa.learningMode': "Mode d'apprentissage",
    'aa.activeLearning': 'Apprentissage actif',
    'aa.passiveLearning': 'Apprentissage passif',
    'aa.supervisedOnly': 'Supervisé uniquement',
    'aa.features': 'Fonctionnalités',
    'aa.trainAI': "Entraîner l'IA",
    'aa.trainingSessionTitle': "Session d'entraînement de l'IA",
    'aa.trainingSessionDesc': "Ajoutez de nouveaux scénarios d'entraînement pour améliorer les réponses de l'IA",
    'aa.customerScenario': 'Scénario client *',
    'aa.scenarioPlaceholder': "Décrivez un problème ou une question du client...",
    'aa.expectedResponse': "Réponse attendue de l'IA *",
    'aa.expectedResponsePlaceholder': "Réponse idéale que l'IA devrait fournir...",
    'aa.difficultyLevel': 'Niveau de difficulté',
    'aa.easy': 'Facile',
    'aa.medium': 'Moyen',
    'aa.hard': 'Difficile',
    'aa.expert': 'Expert',
    'aa.category': 'Catégorie',
    'aa.generalSupport': 'Assistance générale',
    'aa.technicalIssues': 'Problèmes techniques',
    'aa.billingQuestions': 'Questions de facturation',
    'aa.productInformation': 'Informations sur le produit',
    'aa.trainAIModel': "Entraîner le modèle d'IA",
    'aa.currentLoad': 'Charge actuelle',
    'aa.activeAiConversations': "conversations IA actives",
    'aa.avgResponse': 'Réponse moyenne',
    'aa.responseTime': 'temps de réponse',
    'aa.confidenceScore': 'Score de confiance',
    'aa.avgConfidence': 'confiance moyenne',
    'aa.queueLength': "Longueur de la file d'attente",
    'aa.notAvailableYetLower': 'pas encore disponible',
    'aa.tab.conversations': 'Conversations actives',
    'aa.tab.analytics': 'Analyse des performances',
    'aa.tab.knowledge': 'Base de connaissances',
    'aa.tab.learning': 'Apprentissage et avis',
    'aa.realtimeConversations': 'Conversations en temps réel ({count})',
    'aa.refreshConversations': 'Actualiser les conversations',
    'aa.loadingConversations': 'Chargement des conversations...',
    'aa.noActiveConversations': 'Aucune conversation IA active',
    'aa.aiHandlesAutomatically': "L'IA gérera automatiquement les nouvelles conversations",
    'aa.customerInquiry': 'Demande du client',
    'aa.processing': 'Traitement...',
    'aa.justNow': "À l'instant",
    'aa.convDetailUnavailable': "La vue détaillée de la conversation sera disponible dans une prochaine version.",
    'aa.escalate': 'Escalader',
    'aa.escalationUnavailable': "L'escalade depuis le tableau de bord sera disponible dans une prochaine version.",
    'aa.aiStatus': "État de l'IA",
    'aa.telemetryUnavailable': "La télémétrie système en direct n'est pas encore disponible.",
    'aa.quickActions': 'Actions rapides',
    'aa.addKnowledge': 'Ajouter des connaissances',
    'aa.viewAnalytics': 'Voir les analyses',
    'aa.analyticsTitle': "Analyse des performances de l'IA",
    'aa.analyticsDesc': "Métriques et informations complètes sur les performances de l'agent IA",
    'aa.autoResolutionRate': 'Taux de résolution automatique',
    'aa.conversations': 'Conversations',
    'aa.escalationRate': "Taux d'escalade",
    'aa.performanceBreakdown': 'Détail des performances',
    'aa.resolutionRate': 'Taux de résolution',
    'aa.customerSatisfaction': 'Satisfaction client',
    'aa.responseTimeLabel': 'Temps de réponse',
    'aa.trainingLogs': "Journaux d'entraînement",
    'aa.trainingLogsTitle': "Journaux d'entraînement de l'IA",
    'aa.trainingLogsDesc': "Sessions d'entraînement récentes et mises à jour du modèle",
    'aa.noTrainingLogs': "Aucun journal d'entraînement pour le moment",
    'aa.trainingHistoryUpcoming': "L'historique d'entraînement apparaîtra ici dans une prochaine version.",
    'aa.loadingAnalytics': 'Chargement des analyses',
    'aa.loadingAnalyticsDesc': "Génération d'analyses complètes des performances de l'IA...",
    'aa.loadingTrainingLogs': "Chargement des journaux d'entraînement",
    'aa.loadingTrainingLogsDesc': "Récupération de l'historique d'entraînement et des journaux de performances de l'IA...",
    'aa.loadingCaseDetails': 'Chargement des détails du cas',
    'aa.loadingCaseDetailsDesc': 'Récupération des détails du cas n° {id}',
    'aa.resolutionPerformance': 'Performances de résolution',
    'aa.learningProgress': "Progression de l'apprentissage",
    'aa.knowledgeBaseEntries': 'Entrées de la base de connaissances',
    'aa.successfulResolutions': 'Résolutions réussies',
    'aa.totalConversations': 'Conversations totales',
    'aa.knowledgeBaseEntriesCount': 'Entrées de la base de connaissances ({count})',
    'aa.addEntry': 'Ajouter une entrée',
    'aa.noKnowledgeEntries': 'Aucune entrée dans la base de connaissances',
    'aa.addFirstEntry': "Ajoutez votre première entrée à la base de connaissances pour commencer à entraîner l'IA",
    'aa.createFirstEntry': 'Créer la première entrée',
    'aa.uses': '{count} utilisations',
    'aa.recentFeedback': 'Avis récents',
    'aa.noFeedback': "Aucun avis pour le moment",
    'aa.feedbackWillAppear': "Les avis des clients sur les résolutions de l'IA apparaîtront ici.",
    'aa.learningOpportunities': "Opportunités d'apprentissage",
    'aa.improveResolutionRate': 'Améliorer le taux de résolution',
    'aa.improveResolutionDesc': "Examinez les conversations escaladées pour trouver les lacunes que vous pouvez combler avec de nouvelles entrées dans la base de connaissances.",
    'aa.reviewCases': 'Examiner les cas',
    'aa.patternDetection': 'Détection de tendances',
    'aa.patternDetectionDesc': "La détection automatique des tendances et des motifs n'est pas encore disponible.",
    'aa.updateTrainings': 'Mettre à jour les entraînements',
    'aa.addKnowledgeTitle': 'Ajouter une entrée de connaissance',
    'aa.addKnowledgeDesc': "Ajoutez de nouvelles informations à la base de connaissances de l'IA",
    'aa.titleRequired': 'Titre *',
    'aa.knowledgeTitlePlaceholder': "Titre de l'entrée de connaissance",
    'aa.contentRequired': 'Contenu *',
    'aa.contentPlaceholder': 'Contenu détaillé ou solution',
    'aa.technical': 'Technique',
    'aa.billing': 'Facturation',
    'aa.general': 'Général',
    'aa.policy': 'Politique',
    'aa.priority': 'Priorité',
    'aa.low': 'Faible',
    'aa.high': 'Élevée',
    'aa.tags': 'Étiquettes',
    'aa.tagsPlaceholder': 'Étiquettes séparées par des virgules',
    'aa.addToKnowledgeBase': 'Ajouter à la base de connaissances',
    'aa.importTitle': 'Importer la base de connaissances',
    'aa.importDesc': 'Importez des entrées de connaissances depuis un fichier CSV ou JSON',
    'aa.selectFile': 'Sélectionner un fichier',
    'aa.supportedFormats': 'Formats pris en charge : CSV, JSON. Taille maximale du fichier : 10 Mo',
    'aa.exportTitle': 'Exporter la base de connaissances',
    'aa.exportDesc': 'Choisissez le format pour exporter votre base de connaissances',
    'aa.csvFormat': 'Format CSV',
    'aa.csvFormatDesc': 'Compatible avec les tableurs',
    'aa.jsonFormat': 'Format JSON',
    'aa.jsonFormatDesc': 'Données structurées',
    'aa.reviewEscalatedTitle': 'Examiner les cas escaladés',
    'aa.reviewEscalatedDesc': "Examinez et analysez les cas qui ont été escaladés de l'IA vers des agents humains",
    'aa.recentEscalatedCases': 'Cas escaladés récents',
    'aa.noEscalatedCases': 'Aucun cas escaladé à examiner pour le moment',
    'aa.escalatedWillAppear': 'Les conversations escaladées apparaîtront ici dans une prochaine version.',
    'aa.aiConfidenceLabel': "Confiance de l'IA :",
    'aa.escalationAnalysis': "Analyse d'escalade",
    'aa.escalationAnalysisUnavailable': "L'analyse d'escalade n'est pas encore disponible.",
    'aa.generateAnalysisReport': "Générer un rapport d'analyse",
    'aa.analysisReportUnavailable': "La génération de rapports d'analyse sera disponible dans une prochaine version.",
    'aa.caseDetailsTitle': 'Détails du cas - n° {id}',
    'aa.caseDetailsDesc': 'Informations détaillées sur le cas escaladé',
    'aa.caseId': 'ID du cas',
    'aa.customer': 'Client',
    'aa.unknownCustomer': 'Client inconnu',
    'aa.escalated': 'Escaladé',
    'aa.unknownTime': 'Heure inconnue',
    'aa.issueDescription': 'Description du problème',
    'aa.noIssueDescription': 'Aucune description du problème disponible',
    'aa.aiConfidenceLevel': "Niveau de confiance de l'IA",
    'aa.escalationReason': "Motif de l'escalade",
    'aa.escalationReasonText': "Le niveau de confiance de l'IA était inférieur au seuil. Problème technique complexe nécessitant une expertise humaine.",
    'aa.provideFeedbackCaseTitle': 'Donner un avis - Cas n° {id}',
    'aa.provideFeedbackDesc': "Votre avis aide à améliorer les performances de l'IA pour des cas similaires",
    'aa.caseSummary': 'Résumé du cas',
    'aa.feedbackCustomer': 'Client : {name}',
    'aa.feedbackIssue': 'Problème : {issue}',
    'aa.yourFeedback': 'Votre avis *',
    'aa.feedbackPlaceholder': "Comment l'IA aurait-elle pu mieux gérer ce cas ? Quel entraînement ou quelles connaissances supplémentaires aideraient ?",
    'aa.feedbackGuidelines': "Consignes pour les avis :",
    'aa.feedbackGuideline1': "Décrivez ce que l'IA a manqué ou mal compris",
    'aa.feedbackGuideline2': 'Suggérez des améliorations pour la base de connaissances',
    'aa.feedbackGuideline3': 'Notez tout problème de communication avec le client',
    'aa.submitFeedback': "Envoyer l'avis",
    'aa.trainingFillScenario': 'Veuillez remplir le scénario et la réponse attendue',
    'aa.trainingUnavailable': "L'entraînement du modèle d'IA sera disponible dans une prochaine version.",
    'aa.fillTitleContent': 'Veuillez remplir le titre et le contenu',
    'aa.knowledgeAdded': 'Connaissance ajoutée',
    'aa.knowledgeAddedDesc': 'Une nouvelle entrée a été ajoutée à la base de connaissances',
    'aa.addKnowledgeError': "Échec de l'ajout de l'entrée à la base de connaissances",
    'aa.importUnavailable': "L'importation de la base de connaissances sera disponible dans une prochaine version.",
    'aa.nothingToExport': 'Rien à exporter',
    'aa.nothingToExportDesc': "Il n'y a pas encore d'entrées de base de connaissances à exporter.",
    'aa.exportComplete': 'Exportation terminée',
    'aa.exportCompleteDesc': 'Base de connaissances exportée au format {format}.',
    'aa.trainingUpdatesUnavailable': "Les mises à jour d'entraînement de l'IA seront disponibles dans une prochaine version.",
    'aa.feedbackRequired': 'Avis requis',
    'aa.feedbackRequiredDesc': "Veuillez fournir un avis avant d'envoyer.",
    'aa.feedbackSubmitted': 'Avis envoyé',
    'aa.feedbackSubmittedDesc': "L'avis pour le cas n° {id} a été enregistré et contribuera à améliorer les performances de l'IA.",
    'aa.saveConfigError': "Échec de l'enregistrement de la configuration de l'IA",
    'aa.loadingRules': 'Chargement des règles de résolution automatique...',
    'aa.autoResolutionMgmt': 'Gestion de la résolution automatique',
    'aa.autoResolutionSubtitle': 'Configurez des règles de réponse intelligentes et des seuils',
    'aa.activeRulesBadge': '{count} règles actives',
    'aa.aiSettings': "Paramètres de l'IA",
    'aa.aiModel': "Modèle d'IA",
    'aa.advancedReasoning': 'Raisonnement avancé et assistance client intelligente',
    'aa.confidenceThresholdHint': 'Valeurs plus élevées = réponses plus prudentes (0.1-1.0)',
    'aa.advancedFeatures': 'Fonctionnalités avancées',
    'aa.activeLearningFromInteractions': 'Apprentissage actif à partir des interactions avec les clients',
    'aa.activeRules': 'Règles actives',
    'aa.avgSuccessRate': 'Taux de réussite moyen',
    'aa.totalUsage': 'Utilisation totale',
    'aa.advancedAi': 'IA avancée',
    'aa.tab.rules': 'Règles de résolution',
    'aa.tab.create': 'Créer une règle',
    'aa.autoResolutionRulesCount': 'Règles de résolution automatique ({count})',
    'aa.keywordsLabel': 'Mots-clés : {keywords}',
    'aa.confidenceLabel': 'Confiance : {value} %',
    'aa.usedLabel': 'Utilisée : {count} fois',
    'aa.successLabel': 'Réussite : {value} %',
    'aa.createNewRule': 'Créer une nouvelle règle de résolution automatique',
    'aa.ruleName': 'Nom de la règle *',
    'aa.ruleNamePlaceholder': 'Saisissez le nom de la règle',
    'aa.description': 'Description',
    'aa.descriptionPlaceholder': 'Décrivez ce que gère cette règle',
    'aa.keywordsCommaSeparated': 'Mots-clés (séparés par des virgules)',
    'aa.keywordsPlaceholder': 'mot de passe, réinitialiser, oublié, connexion',
    'aa.autoResponse': 'Réponse automatique *',
    'aa.autoResponsePlaceholder': 'Saisissez la réponse automatique pour cette règle',
    'aa.enableRuleImmediately': 'Activer cette règle immédiatement',
    'aa.saveRule': 'Enregistrer la règle',
    'aa.responseLabel': 'Réponse :',
    'aa.aiConfigSettings': 'Paramètres de configuration de IA',
    'aa.aiEngine': 'Moteur IA',
    'aa.poweredBy': 'Propulsé par {provider} ({model})',
    'aa.resolutionAccuracy': 'Précision de résolution',
    'aa.avgResponseTime': 'Temps de réponse moyen',
    'aa.configureAiParams': "Configurer les paramètres de l'IA",
    'aa.editRuleTitle': 'Modifier la règle de résolution automatique',
    'aa.editRuleDesc': 'Modifiez les paramètres de la règle de résolution automatique',
    'aa.ruleIsEnabled': 'La règle est activée',
    'aa.updateRule': 'Mettre à jour la règle',
    'aa.areYouSure': 'Êtes-vous sûr ?',
    'aa.deleteConfirmPre': 'Cette action est irréversible. Elle supprimera définitivement la règle de résolution automatique',
    'aa.deleteConfirmPost': 'et supprimera toutes les données associées.',
    'aa.deleteRule': 'Supprimer la règle',
    'aa.fillRuleNameResponse': 'Veuillez remplir le nom de la règle et la réponse',
    'aa.ruleCreated': 'Règle créée',
    'aa.ruleCreatedDesc': 'La règle de résolution automatique a été créée avec succès',
    'aa.createRuleError': 'Échec de la création de la règle de résolution automatique',
    'aa.ruleUpdated': 'Règle mise à jour',
    'aa.ruleUpdatedDesc': 'La règle de résolution automatique a été mise à jour avec succès',
    'aa.updateRuleError': 'Échec de la mise à jour de la règle de résolution automatique',
    'aa.ruleEnabledSuccess': 'Règle activée avec succès',
    'aa.ruleDisabledSuccess': 'Règle désactivée avec succès',
    'aa.ruleDeleted': 'Règle supprimée',
    'aa.ruleDeletedDesc': 'La règle de résolution automatique a été supprimée avec succès',
    'aa.deleteRuleError': 'Échec de la suppression de la règle de résolution automatique',
    'aa.loadRulesError': 'Échec du chargement des règles de résolution automatique',
    'aa.updateConfigError': "Échec de la mise à jour de la configuration de l'IA",
  },
  pt: {
    'aa.configTitle': 'Configuração do agente de IA',
    'aa.configDesc': 'Configure o comportamento, os limites e os parâmetros operacionais da IA',
    'aa.confidenceThreshold': 'Limite de confiança',
    'aa.maxResponseTime': 'Tempo máximo de resposta (segundos)',
    'aa.saveConfiguration': 'Salvar configuração',
    'aa.autoEscalation': 'Escalonamento automático para problemas complexos',
    'aa.missingInfo': 'Informações incompletas',
    'aa.aiConfigUpdated': 'Configuração de IA atualizada',
    'aa.aiConfigSaved': 'As configurações do agente de IA foram salvas com sucesso',
    'aa.notAvailableYet': 'Ainda não disponível',
    'aa.viewDetails': 'Ver detalhes',
    'aa.provideFeedback': 'Enviar feedback',
    'aa.import': 'Importar',
    'aa.export': 'Exportar',
    'aa.active': 'Ativo',
    'aa.disabled': 'Desativado',
    'aa.dashboardTitle': 'Painel do agente de IA',
    'aa.dashboardSubtitle': 'Sistema inteligente de resolução de primeira linha',
    'aa.managedBy': '| Gerenciado por {name}',
    'aa.configure': 'Configurar',
    'aa.confThreshold70': '70% - Mais respostas, menor precisão',
    'aa.confThreshold80': '80% - Abordagem equilibrada',
    'aa.confThreshold85': '85% - Configuração recomendada',
    'aa.confThreshold90': '90% - Alta precisão, menos respostas',
    'aa.confThreshold95': '95% - Precisão máxima',
    'aa.learningMode': 'Modo de aprendizado',
    'aa.activeLearning': 'Aprendizado ativo',
    'aa.passiveLearning': 'Aprendizado passivo',
    'aa.supervisedOnly': 'Somente supervisionado',
    'aa.features': 'Recursos',
    'aa.trainAI': 'Treinar IA',
    'aa.trainingSessionTitle': 'Sessão de treinamento de IA',
    'aa.trainingSessionDesc': 'Adicione novos cenários de treinamento para melhorar as respostas da IA',
    'aa.customerScenario': 'Cenário do cliente *',
    'aa.scenarioPlaceholder': 'Descreva um problema ou pergunta do cliente...',
    'aa.expectedResponse': 'Resposta esperada da IA *',
    'aa.expectedResponsePlaceholder': 'Resposta ideal que a IA deve fornecer...',
    'aa.difficultyLevel': 'Nível de dificuldade',
    'aa.easy': 'Fácil',
    'aa.medium': 'Médio',
    'aa.hard': 'Difícil',
    'aa.expert': 'Especialista',
    'aa.category': 'Categoria',
    'aa.generalSupport': 'Suporte geral',
    'aa.technicalIssues': 'Problemas técnicos',
    'aa.billingQuestions': 'Perguntas de faturamento',
    'aa.productInformation': 'Informações do produto',
    'aa.trainAIModel': 'Treinar modelo de IA',
    'aa.currentLoad': 'Carga atual',
    'aa.activeAiConversations': 'conversas de IA ativas',
    'aa.avgResponse': 'Resposta média',
    'aa.responseTime': 'tempo de resposta',
    'aa.confidenceScore': 'Pontuação de confiança',
    'aa.avgConfidence': 'confiança média',
    'aa.queueLength': 'Tamanho da fila',
    'aa.notAvailableYetLower': 'ainda não disponível',
    'aa.tab.conversations': 'Conversas ativas',
    'aa.tab.analytics': 'Análise de desempenho',
    'aa.tab.knowledge': 'Base de conhecimento',
    'aa.tab.learning': 'Aprendizado e feedback',
    'aa.realtimeConversations': 'Conversas em tempo real ({count})',
    'aa.refreshConversations': 'Atualizar conversas',
    'aa.loadingConversations': 'Carregando conversas...',
    'aa.noActiveConversations': 'Nenhuma conversa de IA ativa',
    'aa.aiHandlesAutomatically': 'A IA gerenciará automaticamente as novas conversas',
    'aa.customerInquiry': 'Consulta do cliente',
    'aa.processing': 'Processando...',
    'aa.justNow': 'Agora mesmo',
    'aa.convDetailUnavailable': 'A visualização de detalhes da conversa estará disponível em uma próxima versão.',
    'aa.escalate': 'Escalonar',
    'aa.escalationUnavailable': 'O escalonamento pelo painel estará disponível em uma próxima versão.',
    'aa.aiStatus': 'Status da IA',
    'aa.telemetryUnavailable': 'A telemetria do sistema em tempo real ainda não está disponível.',
    'aa.quickActions': 'Ações rápidas',
    'aa.addKnowledge': 'Adicionar conhecimento',
    'aa.viewAnalytics': 'Ver análises',
    'aa.analyticsTitle': 'Análise de desempenho da IA',
    'aa.analyticsDesc': 'Métricas e insights completos do desempenho do agente de IA',
    'aa.autoResolutionRate': 'Taxa de resolução automática',
    'aa.conversations': 'Conversas',
    'aa.escalationRate': 'Taxa de escalonamento',
    'aa.performanceBreakdown': 'Detalhamento do desempenho',
    'aa.resolutionRate': 'Taxa de resolução',
    'aa.customerSatisfaction': 'Satisfação do cliente',
    'aa.responseTimeLabel': 'Tempo de resposta',
    'aa.trainingLogs': 'Registros de treinamento',
    'aa.trainingLogsTitle': 'Registros de treinamento de IA',
    'aa.trainingLogsDesc': 'Sessões de treinamento recentes e atualizações do modelo',
    'aa.noTrainingLogs': 'Ainda não há registros de treinamento',
    'aa.trainingHistoryUpcoming': 'O histórico de treinamento aparecerá aqui em uma próxima versão.',
    'aa.loadingAnalytics': 'Carregando análises',
    'aa.loadingAnalyticsDesc': 'Gerando análises completas de desempenho da IA...',
    'aa.loadingTrainingLogs': 'Carregando registros de treinamento',
    'aa.loadingTrainingLogsDesc': 'Recuperando o histórico de treinamento e os registros de desempenho da IA...',
    'aa.loadingCaseDetails': 'Carregando detalhes do caso',
    'aa.loadingCaseDetailsDesc': 'Recuperando detalhes do caso nº {id}',
    'aa.resolutionPerformance': 'Desempenho de resolução',
    'aa.learningProgress': 'Progresso do aprendizado',
    'aa.knowledgeBaseEntries': 'Entradas da base de conhecimento',
    'aa.successfulResolutions': 'Resoluções bem-sucedidas',
    'aa.totalConversations': 'Conversas totais',
    'aa.knowledgeBaseEntriesCount': 'Entradas da base de conhecimento ({count})',
    'aa.addEntry': 'Adicionar entrada',
    'aa.noKnowledgeEntries': 'Nenhuma entrada na base de conhecimento',
    'aa.addFirstEntry': 'Adicione sua primeira entrada à base de conhecimento para começar a treinar a IA',
    'aa.createFirstEntry': 'Criar primeira entrada',
    'aa.uses': '{count} usos',
    'aa.recentFeedback': 'Feedback recente',
    'aa.noFeedback': 'Ainda não há feedback',
    'aa.feedbackWillAppear': 'O feedback dos clientes sobre as resoluções de IA aparecerá aqui.',
    'aa.learningOpportunities': 'Oportunidades de aprendizado',
    'aa.improveResolutionRate': 'Melhorar a taxa de resolução',
    'aa.improveResolutionDesc': 'Revise as conversas escalonadas para encontrar lacunas que você pode preencher com novas entradas na base de conhecimento.',
    'aa.reviewCases': 'Revisar casos',
    'aa.patternDetection': 'Detecção de padrões',
    'aa.patternDetectionDesc': 'A detecção automática de tendências e padrões ainda não está disponível.',
    'aa.updateTrainings': 'Atualizar treinamentos',
    'aa.addKnowledgeTitle': 'Adicionar entrada de conhecimento',
    'aa.addKnowledgeDesc': 'Adicione novas informações à base de conhecimento da IA',
    'aa.titleRequired': 'Título *',
    'aa.knowledgeTitlePlaceholder': 'Título da entrada de conhecimento',
    'aa.contentRequired': 'Conteúdo *',
    'aa.contentPlaceholder': 'Conteúdo detalhado ou solução',
    'aa.technical': 'Técnico',
    'aa.billing': 'Faturamento',
    'aa.general': 'Geral',
    'aa.policy': 'Política',
    'aa.priority': 'Prioridade',
    'aa.low': 'Baixa',
    'aa.high': 'Alta',
    'aa.tags': 'Tags',
    'aa.tagsPlaceholder': 'Tags separadas por vírgulas',
    'aa.addToKnowledgeBase': 'Adicionar à base de conhecimento',
    'aa.importTitle': 'Importar base de conhecimento',
    'aa.importDesc': 'Importe entradas de conhecimento de um arquivo CSV ou JSON',
    'aa.selectFile': 'Selecionar arquivo',
    'aa.supportedFormats': 'Formatos suportados: CSV, JSON. Tamanho máximo do arquivo: 10 MB',
    'aa.exportTitle': 'Exportar base de conhecimento',
    'aa.exportDesc': 'Escolha o formato para exportar sua base de conhecimento',
    'aa.csvFormat': 'Formato CSV',
    'aa.csvFormatDesc': 'Compatível com planilhas',
    'aa.jsonFormat': 'Formato JSON',
    'aa.jsonFormatDesc': 'Dados estruturados',
    'aa.reviewEscalatedTitle': 'Revisar casos escalonados',
    'aa.reviewEscalatedDesc': 'Revise e analise os casos que foram escalonados da IA para agentes humanos',
    'aa.recentEscalatedCases': 'Casos escalonados recentes',
    'aa.noEscalatedCases': 'Ainda não há casos escalonados para revisar',
    'aa.escalatedWillAppear': 'As conversas escalonadas aparecerão aqui em uma próxima versão.',
    'aa.aiConfidenceLabel': 'Confiança da IA:',
    'aa.escalationAnalysis': 'Análise de escalonamento',
    'aa.escalationAnalysisUnavailable': 'A análise de escalonamento ainda não está disponível.',
    'aa.generateAnalysisReport': 'Gerar relatório de análise',
    'aa.analysisReportUnavailable': 'A geração de relatórios de análise estará disponível em uma próxima versão.',
    'aa.caseDetailsTitle': 'Detalhes do caso - nº {id}',
    'aa.caseDetailsDesc': 'Informações detalhadas sobre o caso escalonado',
    'aa.caseId': 'ID do caso',
    'aa.customer': 'Cliente',
    'aa.unknownCustomer': 'Cliente desconhecido',
    'aa.escalated': 'Escalonado',
    'aa.unknownTime': 'Horário desconhecido',
    'aa.issueDescription': 'Descrição do problema',
    'aa.noIssueDescription': 'Nenhuma descrição do problema disponível',
    'aa.aiConfidenceLevel': 'Nível de confiança da IA',
    'aa.escalationReason': 'Motivo do escalonamento',
    'aa.escalationReasonText': 'O nível de confiança da IA estava abaixo do limite. Problema técnico complexo que requer conhecimento humano.',
    'aa.provideFeedbackCaseTitle': 'Enviar feedback - Caso nº {id}',
    'aa.provideFeedbackDesc': 'Seu feedback ajuda a melhorar o desempenho da IA em casos semelhantes',
    'aa.caseSummary': 'Resumo do caso',
    'aa.feedbackCustomer': 'Cliente: {name}',
    'aa.feedbackIssue': 'Problema: {issue}',
    'aa.yourFeedback': 'Seu feedback *',
    'aa.feedbackPlaceholder': 'Como a IA poderia ter lidado melhor com este caso? Que treinamento ou conhecimento adicional ajudaria?',
    'aa.feedbackGuidelines': 'Diretrizes de feedback:',
    'aa.feedbackGuideline1': 'Descreva o que a IA não percebeu ou entendeu mal',
    'aa.feedbackGuideline2': 'Sugira melhorias na base de conhecimento',
    'aa.feedbackGuideline3': 'Anote quaisquer problemas de comunicação com o cliente',
    'aa.submitFeedback': 'Enviar feedback',
    'aa.trainingFillScenario': 'Preencha o cenário e a resposta esperada',
    'aa.trainingUnavailable': 'O treinamento do modelo de IA estará disponível em uma próxima versão.',
    'aa.fillTitleContent': 'Preencha o título e o conteúdo',
    'aa.knowledgeAdded': 'Conhecimento adicionado',
    'aa.knowledgeAddedDesc': 'Uma nova entrada foi adicionada à base de conhecimento',
    'aa.addKnowledgeError': 'Falha ao adicionar a entrada à base de conhecimento',
    'aa.importUnavailable': 'A importação da base de conhecimento estará disponível em uma próxima versão.',
    'aa.nothingToExport': 'Nada para exportar',
    'aa.nothingToExportDesc': 'Ainda não há entradas na base de conhecimento para exportar.',
    'aa.exportComplete': 'Exportação concluída',
    'aa.exportCompleteDesc': 'Base de conhecimento exportada como {format}.',
    'aa.trainingUpdatesUnavailable': 'As atualizações de treinamento de IA estarão disponíveis em uma próxima versão.',
    'aa.feedbackRequired': 'Feedback obrigatório',
    'aa.feedbackRequiredDesc': 'Forneça feedback antes de enviar.',
    'aa.feedbackSubmitted': 'Feedback enviado',
    'aa.feedbackSubmittedDesc': 'O feedback do caso nº {id} foi registrado e ajudará a melhorar o desempenho da IA.',
    'aa.saveConfigError': 'Falha ao salvar a configuração de IA',
    'aa.loadingRules': 'Carregando regras de resolução automática...',
    'aa.autoResolutionMgmt': 'Gerenciamento de resolução automática',
    'aa.autoResolutionSubtitle': 'Configure regras de resposta inteligentes e limites',
    'aa.activeRulesBadge': '{count} regras ativas',
    'aa.aiSettings': 'Configurações de IA',
    'aa.aiModel': 'Modelo de IA',
    'aa.advancedReasoning': 'Raciocínio avançado e suporte inteligente ao cliente',
    'aa.confidenceThresholdHint': 'Valores mais altos = respostas mais cuidadosas (0.1-1.0)',
    'aa.advancedFeatures': 'Recursos avançados',
    'aa.activeLearningFromInteractions': 'Aprendizado ativo a partir das interações com os clientes',
    'aa.activeRules': 'Regras ativas',
    'aa.avgSuccessRate': 'Taxa média de sucesso',
    'aa.totalUsage': 'Uso total',
    'aa.advancedAi': 'IA avançada',
    'aa.tab.rules': 'Regras de resolução',
    'aa.tab.create': 'Criar regra',
    'aa.autoResolutionRulesCount': 'Regras de resolução automática ({count})',
    'aa.keywordsLabel': 'Palavras-chave: {keywords}',
    'aa.confidenceLabel': 'Confiança: {value}%',
    'aa.usedLabel': 'Usada: {count} vezes',
    'aa.successLabel': 'Sucesso: {value}%',
    'aa.createNewRule': 'Criar nova regra de resolução automática',
    'aa.ruleName': 'Nome da regra *',
    'aa.ruleNamePlaceholder': 'Digite o nome da regra',
    'aa.description': 'Descrição',
    'aa.descriptionPlaceholder': 'Descreva o que esta regra trata',
    'aa.keywordsCommaSeparated': 'Palavras-chave (separadas por vírgulas)',
    'aa.keywordsPlaceholder': 'senha, redefinir, esqueci, login',
    'aa.autoResponse': 'Resposta automática *',
    'aa.autoResponsePlaceholder': 'Digite a resposta automática para esta regra',
    'aa.enableRuleImmediately': 'Ativar esta regra imediatamente',
    'aa.saveRule': 'Salvar regra',
    'aa.responseLabel': 'Resposta:',
    'aa.aiConfigSettings': 'Configurações de IA',
    'aa.aiEngine': 'Motor de IA',
    'aa.poweredBy': 'Desenvolvido com {provider} ({model})',
    'aa.resolutionAccuracy': 'Precisão de resolução',
    'aa.avgResponseTime': 'Tempo médio de resposta',
    'aa.configureAiParams': 'Configurar parâmetros de IA',
    'aa.editRuleTitle': 'Editar regra de resolução automática',
    'aa.editRuleDesc': 'Modifique as configurações da regra de resolução automática',
    'aa.ruleIsEnabled': 'A regra está ativada',
    'aa.updateRule': 'Atualizar regra',
    'aa.areYouSure': 'Tem certeza?',
    'aa.deleteConfirmPre': 'Esta ação não pode ser desfeita. Isso excluirá permanentemente a regra de resolução automática',
    'aa.deleteConfirmPost': 'e removerá todos os dados associados.',
    'aa.deleteRule': 'Excluir regra',
    'aa.fillRuleNameResponse': 'Preencha o nome da regra e a resposta',
    'aa.ruleCreated': 'Regra criada',
    'aa.ruleCreatedDesc': 'A regra de resolução automática foi criada com sucesso',
    'aa.createRuleError': 'Falha ao criar a regra de resolução automática',
    'aa.ruleUpdated': 'Regra atualizada',
    'aa.ruleUpdatedDesc': 'A regra de resolução automática foi atualizada com sucesso',
    'aa.updateRuleError': 'Falha ao atualizar a regra de resolução automática',
    'aa.ruleEnabledSuccess': 'Regra ativada com sucesso',
    'aa.ruleDisabledSuccess': 'Regra desativada com sucesso',
    'aa.ruleDeleted': 'Regra excluída',
    'aa.ruleDeletedDesc': 'A regra de resolução automática foi excluída com sucesso',
    'aa.deleteRuleError': 'Falha ao excluir a regra de resolução automática',
    'aa.loadRulesError': 'Falha ao carregar as regras de resolução automática',
    'aa.updateConfigError': 'Falha ao atualizar a configuração de IA',
  },
  de: {
    'aa.configTitle': 'KI-Agent-Konfiguration',
    'aa.configDesc': 'Konfigurieren Sie das KI-Verhalten, Schwellenwerte und Betriebsparameter',
    'aa.confidenceThreshold': 'Konfidenzschwelle',
    'aa.maxResponseTime': 'Maximale Antwortzeit (Sekunden)',
    'aa.saveConfiguration': 'Konfiguration speichern',
    'aa.autoEscalation': 'Automatische Eskalation bei komplexen Problemen',
    'aa.missingInfo': 'Fehlende Informationen',
    'aa.aiConfigUpdated': 'KI-Konfiguration aktualisiert',
    'aa.aiConfigSaved': 'Die Einstellungen des KI-Agenten wurden erfolgreich gespeichert',
    'aa.notAvailableYet': 'Noch nicht verfügbar',
    'aa.viewDetails': 'Details anzeigen',
    'aa.provideFeedback': 'Feedback geben',
    'aa.import': 'Importieren',
    'aa.export': 'Exportieren',
    'aa.active': 'Aktiv',
    'aa.disabled': 'Deaktiviert',
    'aa.dashboardTitle': 'KI-Agent-Dashboard',
    'aa.dashboardSubtitle': 'Intelligentes First-Line-Lösungssystem',
    'aa.managedBy': '| Verwaltet von {name}',
    'aa.configure': 'Konfigurieren',
    'aa.confThreshold70': '70 % - Mehr Antworten, geringere Genauigkeit',
    'aa.confThreshold80': '80 % - Ausgewogener Ansatz',
    'aa.confThreshold85': '85 % - Empfohlene Einstellung',
    'aa.confThreshold90': '90 % - Hohe Genauigkeit, weniger Antworten',
    'aa.confThreshold95': '95 % - Maximale Genauigkeit',
    'aa.learningMode': 'Lernmodus',
    'aa.activeLearning': 'Aktives Lernen',
    'aa.passiveLearning': 'Passives Lernen',
    'aa.supervisedOnly': 'Nur überwacht',
    'aa.features': 'Funktionen',
    'aa.trainAI': 'KI trainieren',
    'aa.trainingSessionTitle': 'KI-Trainingssitzung',
    'aa.trainingSessionDesc': 'Fügen Sie neue Trainingsszenarien hinzu, um die KI-Antworten zu verbessern',
    'aa.customerScenario': 'Kundenszenario *',
    'aa.scenarioPlaceholder': 'Beschreiben Sie ein Kundenproblem oder eine Frage...',
    'aa.expectedResponse': 'Erwartete KI-Antwort *',
    'aa.expectedResponsePlaceholder': 'Ideale Antwort, die die KI geben sollte...',
    'aa.difficultyLevel': 'Schwierigkeitsgrad',
    'aa.easy': 'Einfach',
    'aa.medium': 'Mittel',
    'aa.hard': 'Schwer',
    'aa.expert': 'Experte',
    'aa.category': 'Kategorie',
    'aa.generalSupport': 'Allgemeiner Support',
    'aa.technicalIssues': 'Technische Probleme',
    'aa.billingQuestions': 'Abrechnungsfragen',
    'aa.productInformation': 'Produktinformationen',
    'aa.trainAIModel': 'KI-Modell trainieren',
    'aa.currentLoad': 'Aktuelle Auslastung',
    'aa.activeAiConversations': 'aktive KI-Konversationen',
    'aa.avgResponse': 'Durchschn. Antwort',
    'aa.responseTime': 'Antwortzeit',
    'aa.confidenceScore': 'Konfidenzwert',
    'aa.avgConfidence': 'durchschn. Konfidenz',
    'aa.queueLength': 'Warteschlangenlänge',
    'aa.notAvailableYetLower': 'noch nicht verfügbar',
    'aa.tab.conversations': 'Aktive Konversationen',
    'aa.tab.analytics': 'Leistungsanalyse',
    'aa.tab.knowledge': 'Wissensdatenbank',
    'aa.tab.learning': 'Lernen & Feedback',
    'aa.realtimeConversations': 'Echtzeit-Konversationen ({count})',
    'aa.refreshConversations': 'Konversationen aktualisieren',
    'aa.loadingConversations': 'Konversationen werden geladen...',
    'aa.noActiveConversations': 'Keine aktiven KI-Konversationen',
    'aa.aiHandlesAutomatically': 'Die KI bearbeitet neue Konversationen automatisch',
    'aa.customerInquiry': 'Kundenanfrage',
    'aa.processing': 'Wird verarbeitet...',
    'aa.justNow': 'Gerade eben',
    'aa.convDetailUnavailable': 'Die Detailansicht der Konversation wird in einer kommenden Version verfügbar sein.',
    'aa.escalate': 'Eskalieren',
    'aa.escalationUnavailable': 'Die Eskalation im Dashboard wird in einer kommenden Version verfügbar sein.',
    'aa.aiStatus': 'KI-Status',
    'aa.telemetryUnavailable': 'Live-Systemtelemetrie ist noch nicht verfügbar.',
    'aa.quickActions': 'Schnellaktionen',
    'aa.addKnowledge': 'Wissen hinzufügen',
    'aa.viewAnalytics': 'Analysen anzeigen',
    'aa.analyticsTitle': 'KI-Leistungsanalyse',
    'aa.analyticsDesc': 'Umfassende Leistungsmetriken und Einblicke des KI-Agenten',
    'aa.autoResolutionRate': 'Automatische Lösungsrate',
    'aa.conversations': 'Konversationen',
    'aa.escalationRate': 'Eskalationsrate',
    'aa.performanceBreakdown': 'Leistungsaufschlüsselung',
    'aa.resolutionRate': 'Lösungsrate',
    'aa.customerSatisfaction': 'Kundenzufriedenheit',
    'aa.responseTimeLabel': 'Antwortzeit',
    'aa.trainingLogs': 'Trainingsprotokolle',
    'aa.trainingLogsTitle': 'KI-Trainingsprotokolle',
    'aa.trainingLogsDesc': 'Aktuelle Trainingssitzungen und Modellaktualisierungen',
    'aa.noTrainingLogs': 'Noch keine Trainingsprotokolle',
    'aa.trainingHistoryUpcoming': 'Der Trainingsverlauf wird hier in einer kommenden Version angezeigt.',
    'aa.loadingAnalytics': 'Analysen werden geladen',
    'aa.loadingAnalyticsDesc': 'Umfassende KI-Leistungsanalysen werden generiert...',
    'aa.loadingTrainingLogs': 'Trainingsprotokolle werden geladen',
    'aa.loadingTrainingLogsDesc': 'KI-Trainingsverlauf und Leistungsprotokolle werden abgerufen...',
    'aa.loadingCaseDetails': 'Falldetails werden geladen',
    'aa.loadingCaseDetailsDesc': 'Details für Fall Nr. {id} werden abgerufen',
    'aa.resolutionPerformance': 'Lösungsleistung',
    'aa.learningProgress': 'Lernfortschritt',
    'aa.knowledgeBaseEntries': 'Wissensdatenbank-Einträge',
    'aa.successfulResolutions': 'Erfolgreiche Lösungen',
    'aa.totalConversations': 'Gesamte Konversationen',
    'aa.knowledgeBaseEntriesCount': 'Wissensdatenbank-Einträge ({count})',
    'aa.addEntry': 'Eintrag hinzufügen',
    'aa.noKnowledgeEntries': 'Keine Wissensdatenbank-Einträge',
    'aa.addFirstEntry': 'Fügen Sie Ihren ersten Wissensdatenbank-Eintrag hinzu, um mit dem Training der KI zu beginnen',
    'aa.createFirstEntry': 'Ersten Eintrag erstellen',
    'aa.uses': '{count} Verwendungen',
    'aa.recentFeedback': 'Aktuelles Feedback',
    'aa.noFeedback': 'Noch kein Feedback',
    'aa.feedbackWillAppear': 'Kundenfeedback zu KI-Lösungen wird hier angezeigt.',
    'aa.learningOpportunities': 'Lernmöglichkeiten',
    'aa.improveResolutionRate': 'Lösungsrate verbessern',
    'aa.improveResolutionDesc': 'Überprüfen Sie eskalierte Konversationen, um Lücken zu finden, die Sie mit neuen Wissensdatenbank-Einträgen schließen können.',
    'aa.reviewCases': 'Fälle überprüfen',
    'aa.patternDetection': 'Mustererkennung',
    'aa.patternDetectionDesc': 'Die automatische Trend- und Mustererkennung ist noch nicht verfügbar.',
    'aa.updateTrainings': 'Trainings aktualisieren',
    'aa.addKnowledgeTitle': 'Wissenseintrag hinzufügen',
    'aa.addKnowledgeDesc': 'Fügen Sie neue Informationen zur KI-Wissensdatenbank hinzu',
    'aa.titleRequired': 'Titel *',
    'aa.knowledgeTitlePlaceholder': 'Titel des Wissenseintrags',
    'aa.contentRequired': 'Inhalt *',
    'aa.contentPlaceholder': 'Detaillierter Inhalt oder Lösung',
    'aa.technical': 'Technisch',
    'aa.billing': 'Abrechnung',
    'aa.general': 'Allgemein',
    'aa.policy': 'Richtlinie',
    'aa.priority': 'Priorität',
    'aa.low': 'Niedrig',
    'aa.high': 'Hoch',
    'aa.tags': 'Tags',
    'aa.tagsPlaceholder': 'Durch Kommas getrennte Tags',
    'aa.addToKnowledgeBase': 'Zur Wissensdatenbank hinzufügen',
    'aa.importTitle': 'Wissensdatenbank importieren',
    'aa.importDesc': 'Importieren Sie Wissenseinträge aus einer CSV- oder JSON-Datei',
    'aa.selectFile': 'Datei auswählen',
    'aa.supportedFormats': 'Unterstützte Formate: CSV, JSON. Maximale Dateigröße: 10 MB',
    'aa.exportTitle': 'Wissensdatenbank exportieren',
    'aa.exportDesc': 'Wählen Sie das Format zum Exportieren Ihrer Wissensdatenbank',
    'aa.csvFormat': 'CSV-Format',
    'aa.csvFormatDesc': 'Tabellenkalkulationskompatibel',
    'aa.jsonFormat': 'JSON-Format',
    'aa.jsonFormatDesc': 'Strukturierte Daten',
    'aa.reviewEscalatedTitle': 'Eskalierte Fälle überprüfen',
    'aa.reviewEscalatedDesc': 'Überprüfen und analysieren Sie Fälle, die von der KI an menschliche Agenten eskaliert wurden',
    'aa.recentEscalatedCases': 'Aktuelle eskalierte Fälle',
    'aa.noEscalatedCases': 'Noch keine eskalierten Fälle zum Überprüfen',
    'aa.escalatedWillAppear': 'Eskalierte Konversationen werden hier in einer kommenden Version angezeigt.',
    'aa.aiConfidenceLabel': 'KI-Konfidenz:',
    'aa.escalationAnalysis': 'Eskalationsanalyse',
    'aa.escalationAnalysisUnavailable': 'Die Eskalationsanalyse ist noch nicht verfügbar.',
    'aa.generateAnalysisReport': 'Analysebericht erstellen',
    'aa.analysisReportUnavailable': 'Die Erstellung von Analyseberichten wird in einer kommenden Version verfügbar sein.',
    'aa.caseDetailsTitle': 'Falldetails - Nr. {id}',
    'aa.caseDetailsDesc': 'Detaillierte Informationen zum eskalierten Fall',
    'aa.caseId': 'Fall-ID',
    'aa.customer': 'Kunde',
    'aa.unknownCustomer': 'Unbekannter Kunde',
    'aa.escalated': 'Eskaliert',
    'aa.unknownTime': 'Unbekannte Zeit',
    'aa.issueDescription': 'Problembeschreibung',
    'aa.noIssueDescription': 'Keine Problembeschreibung verfügbar',
    'aa.aiConfidenceLevel': 'KI-Konfidenzniveau',
    'aa.escalationReason': 'Eskalationsgrund',
    'aa.escalationReasonText': 'Das KI-Konfidenzniveau lag unter dem Schwellenwert. Komplexes technisches Problem, das menschliches Fachwissen erfordert.',
    'aa.provideFeedbackCaseTitle': 'Feedback geben - Fall Nr. {id}',
    'aa.provideFeedbackDesc': 'Ihr Feedback hilft, die KI-Leistung bei ähnlichen Fällen zu verbessern',
    'aa.caseSummary': 'Fallzusammenfassung',
    'aa.feedbackCustomer': 'Kunde: {name}',
    'aa.feedbackIssue': 'Problem: {issue}',
    'aa.yourFeedback': 'Ihr Feedback *',
    'aa.feedbackPlaceholder': 'Wie hätte die KI diesen Fall besser bearbeiten können? Welches zusätzliche Training oder Wissen würde helfen?',
    'aa.feedbackGuidelines': 'Feedback-Richtlinien:',
    'aa.feedbackGuideline1': 'Beschreiben Sie, was die KI übersehen oder falsch verstanden hat',
    'aa.feedbackGuideline2': 'Schlagen Sie Verbesserungen für die Wissensdatenbank vor',
    'aa.feedbackGuideline3': 'Notieren Sie etwaige Kommunikationsprobleme mit dem Kunden',
    'aa.submitFeedback': 'Feedback senden',
    'aa.trainingFillScenario': 'Bitte füllen Sie Szenario und erwartete Antwort aus',
    'aa.trainingUnavailable': 'Das Training des KI-Modells wird in einer kommenden Version verfügbar sein.',
    'aa.fillTitleContent': 'Bitte füllen Sie Titel und Inhalt aus',
    'aa.knowledgeAdded': 'Wissen hinzugefügt',
    'aa.knowledgeAddedDesc': 'Ein neuer Eintrag wurde zur Wissensdatenbank hinzugefügt',
    'aa.addKnowledgeError': 'Der Wissensdatenbank-Eintrag konnte nicht hinzugefügt werden',
    'aa.importUnavailable': 'Der Import der Wissensdatenbank wird in einer kommenden Version verfügbar sein.',
    'aa.nothingToExport': 'Nichts zu exportieren',
    'aa.nothingToExportDesc': 'Es gibt noch keine Wissensdatenbank-Einträge zum Exportieren.',
    'aa.exportComplete': 'Export abgeschlossen',
    'aa.exportCompleteDesc': 'Wissensdatenbank als {format} exportiert.',
    'aa.trainingUpdatesUnavailable': 'KI-Trainingsaktualisierungen werden in einer kommenden Version verfügbar sein.',
    'aa.feedbackRequired': 'Feedback erforderlich',
    'aa.feedbackRequiredDesc': 'Bitte geben Sie vor dem Senden Feedback.',
    'aa.feedbackSubmitted': 'Feedback gesendet',
    'aa.feedbackSubmittedDesc': 'Das Feedback für Fall Nr. {id} wurde erfasst und trägt zur Verbesserung der KI-Leistung bei.',
    'aa.saveConfigError': 'Die KI-Konfiguration konnte nicht gespeichert werden',
    'aa.loadingRules': 'Automatische Lösungsregeln werden geladen...',
    'aa.autoResolutionMgmt': 'Verwaltung der automatischen Lösung',
    'aa.autoResolutionSubtitle': 'Konfigurieren Sie intelligente Antwortregeln und Schwellenwerte',
    'aa.activeRulesBadge': '{count} aktive Regeln',
    'aa.aiSettings': 'KI-Einstellungen',
    'aa.aiModel': 'KI-Modell',
    'aa.advancedReasoning': 'Fortgeschrittenes Denken und intelligenter Kundensupport',
    'aa.confidenceThresholdHint': 'Höhere Werte = sorgfältigere Antworten (0.1-1.0)',
    'aa.advancedFeatures': 'Erweiterte Funktionen',
    'aa.activeLearningFromInteractions': 'Aktives Lernen aus Kundeninteraktionen',
    'aa.activeRules': 'Aktive Regeln',
    'aa.avgSuccessRate': 'Durchschn. Erfolgsrate',
    'aa.totalUsage': 'Gesamtnutzung',
    'aa.advancedAi': 'Fortgeschrittene KI',
    'aa.tab.rules': 'Lösungsregeln',
    'aa.tab.create': 'Regel erstellen',
    'aa.autoResolutionRulesCount': 'Automatische Lösungsregeln ({count})',
    'aa.keywordsLabel': 'Schlüsselwörter: {keywords}',
    'aa.confidenceLabel': 'Konfidenz: {value} %',
    'aa.usedLabel': 'Verwendet: {count} Mal',
    'aa.successLabel': 'Erfolg: {value} %',
    'aa.createNewRule': 'Neue automatische Lösungsregel erstellen',
    'aa.ruleName': 'Regelname *',
    'aa.ruleNamePlaceholder': 'Regelnamen eingeben',
    'aa.description': 'Beschreibung',
    'aa.descriptionPlaceholder': 'Beschreiben Sie, was diese Regel behandelt',
    'aa.keywordsCommaSeparated': 'Schlüsselwörter (durch Kommas getrennt)',
    'aa.keywordsPlaceholder': 'Passwort, zurücksetzen, vergessen, Anmeldung',
    'aa.autoResponse': 'Automatische Antwort *',
    'aa.autoResponsePlaceholder': 'Geben Sie die automatische Antwort für diese Regel ein',
    'aa.enableRuleImmediately': 'Diese Regel sofort aktivieren',
    'aa.saveRule': 'Regel speichern',
    'aa.responseLabel': 'Antwort:',
    'aa.aiConfigSettings': 'KI-Konfigurationseinstellungen',
    'aa.aiEngine': 'KI-Engine',
    'aa.poweredBy': 'Unterstützt von {provider} ({model})',
    'aa.resolutionAccuracy': 'Lösungsgenauigkeit',
    'aa.avgResponseTime': 'Durchschn. Antwortzeit',
    'aa.configureAiParams': 'KI-Parameter konfigurieren',
    'aa.editRuleTitle': 'Automatische Lösungsregel bearbeiten',
    'aa.editRuleDesc': 'Ändern Sie die Einstellungen der automatischen Lösungsregel',
    'aa.ruleIsEnabled': 'Regel ist aktiviert',
    'aa.updateRule': 'Regel aktualisieren',
    'aa.areYouSure': 'Sind Sie sicher?',
    'aa.deleteConfirmPre': 'Diese Aktion kann nicht rückgängig gemacht werden. Dadurch wird die automatische Lösungsregel dauerhaft gelöscht',
    'aa.deleteConfirmPost': 'und alle zugehörigen Daten werden entfernt.',
    'aa.deleteRule': 'Regel löschen',
    'aa.fillRuleNameResponse': 'Bitte füllen Sie Regelname und Antwort aus',
    'aa.ruleCreated': 'Regel erstellt',
    'aa.ruleCreatedDesc': 'Die automatische Lösungsregel wurde erfolgreich erstellt',
    'aa.createRuleError': 'Die automatische Lösungsregel konnte nicht erstellt werden',
    'aa.ruleUpdated': 'Regel aktualisiert',
    'aa.ruleUpdatedDesc': 'Die automatische Lösungsregel wurde erfolgreich aktualisiert',
    'aa.updateRuleError': 'Die automatische Lösungsregel konnte nicht aktualisiert werden',
    'aa.ruleEnabledSuccess': 'Regel erfolgreich aktiviert',
    'aa.ruleDisabledSuccess': 'Regel erfolgreich deaktiviert',
    'aa.ruleDeleted': 'Regel gelöscht',
    'aa.ruleDeletedDesc': 'Die automatische Lösungsregel wurde erfolgreich gelöscht',
    'aa.deleteRuleError': 'Die automatische Lösungsregel konnte nicht gelöscht werden',
    'aa.loadRulesError': 'Die automatischen Lösungsregeln konnten nicht geladen werden',
    'aa.updateConfigError': 'Die KI-Konfiguration konnte nicht aktualisiert werden',
  },
  it: {
    'aa.configTitle': "Configurazione dell'agente IA",
    'aa.configDesc': "Configura il comportamento, le soglie e i parametri operativi dell'IA",
    'aa.confidenceThreshold': 'Soglia di confidenza',
    'aa.maxResponseTime': 'Tempo massimo di risposta (secondi)',
    'aa.saveConfiguration': 'Salva configurazione',
    'aa.autoEscalation': 'Escalation automatica per problemi complessi',
    'aa.missingInfo': 'Informazioni mancanti',
    'aa.aiConfigUpdated': 'Configurazione IA aggiornata',
    'aa.aiConfigSaved': "Le impostazioni dell'agente IA sono state salvate correttamente",
    'aa.notAvailableYet': 'Non ancora disponibile',
    'aa.viewDetails': 'Visualizza dettagli',
    'aa.provideFeedback': 'Fornisci feedback',
    'aa.import': 'Importa',
    'aa.export': 'Esporta',
    'aa.active': 'Attivo',
    'aa.disabled': 'Disattivato',
    'aa.dashboardTitle': "Dashboard dell'agente IA",
    'aa.dashboardSubtitle': 'Sistema intelligente di risoluzione di primo livello',
    'aa.managedBy': '| Gestito da {name}',
    'aa.configure': 'Configura',
    'aa.confThreshold70': '70% - Più risposte, minore precisione',
    'aa.confThreshold80': '80% - Approccio bilanciato',
    'aa.confThreshold85': '85% - Impostazione consigliata',
    'aa.confThreshold90': '90% - Alta precisione, meno risposte',
    'aa.confThreshold95': '95% - Precisione massima',
    'aa.learningMode': 'Modalità di apprendimento',
    'aa.activeLearning': 'Apprendimento attivo',
    'aa.passiveLearning': 'Apprendimento passivo',
    'aa.supervisedOnly': 'Solo supervisionato',
    'aa.features': 'Funzionalità',
    'aa.trainAI': "Addestra l'IA",
    'aa.trainingSessionTitle': "Sessione di addestramento dell'IA",
    'aa.trainingSessionDesc': "Aggiungi nuovi scenari di addestramento per migliorare le risposte dell'IA",
    'aa.customerScenario': 'Scenario del cliente *',
    'aa.scenarioPlaceholder': 'Descrivi un problema o una domanda del cliente...',
    'aa.expectedResponse': "Risposta prevista dell'IA *",
    'aa.expectedResponsePlaceholder': "Risposta ideale che l'IA dovrebbe fornire...",
    'aa.difficultyLevel': 'Livello di difficoltà',
    'aa.easy': 'Facile',
    'aa.medium': 'Medio',
    'aa.hard': 'Difficile',
    'aa.expert': 'Esperto',
    'aa.category': 'Categoria',
    'aa.generalSupport': 'Assistenza generale',
    'aa.technicalIssues': 'Problemi tecnici',
    'aa.billingQuestions': 'Domande sulla fatturazione',
    'aa.productInformation': 'Informazioni sul prodotto',
    'aa.trainAIModel': "Addestra il modello di IA",
    'aa.currentLoad': 'Carico attuale',
    'aa.activeAiConversations': 'conversazioni IA attive',
    'aa.avgResponse': 'Risposta media',
    'aa.responseTime': 'tempo di risposta',
    'aa.confidenceScore': 'Punteggio di confidenza',
    'aa.avgConfidence': 'confidenza media',
    'aa.queueLength': 'Lunghezza della coda',
    'aa.notAvailableYetLower': 'non ancora disponibile',
    'aa.tab.conversations': 'Conversazioni attive',
    'aa.tab.analytics': 'Analisi delle prestazioni',
    'aa.tab.knowledge': 'Base di conoscenza',
    'aa.tab.learning': 'Apprendimento e feedback',
    'aa.realtimeConversations': 'Conversazioni in tempo reale ({count})',
    'aa.refreshConversations': 'Aggiorna conversazioni',
    'aa.loadingConversations': 'Caricamento delle conversazioni...',
    'aa.noActiveConversations': 'Nessuna conversazione IA attiva',
    'aa.aiHandlesAutomatically': "L'IA gestirà automaticamente le nuove conversazioni",
    'aa.customerInquiry': 'Richiesta del cliente',
    'aa.processing': 'Elaborazione...',
    'aa.justNow': 'Proprio ora',
    'aa.convDetailUnavailable': 'La visualizzazione dei dettagli della conversazione sarà disponibile in una prossima versione.',
    'aa.escalate': 'Inoltra',
    'aa.escalationUnavailable': "L'inoltro dalla dashboard sarà disponibile in una prossima versione.",
    'aa.aiStatus': "Stato dell'IA",
    'aa.telemetryUnavailable': 'La telemetria di sistema in tempo reale non è ancora disponibile.',
    'aa.quickActions': 'Azioni rapide',
    'aa.addKnowledge': 'Aggiungi conoscenza',
    'aa.viewAnalytics': 'Visualizza analisi',
    'aa.analyticsTitle': "Analisi delle prestazioni dell'IA",
    'aa.analyticsDesc': "Metriche e approfondimenti completi sulle prestazioni dell'agente IA",
    'aa.autoResolutionRate': 'Tasso di risoluzione automatica',
    'aa.conversations': 'Conversazioni',
    'aa.escalationRate': 'Tasso di escalation',
    'aa.performanceBreakdown': 'Dettaglio delle prestazioni',
    'aa.resolutionRate': 'Tasso di risoluzione',
    'aa.customerSatisfaction': 'Soddisfazione del cliente',
    'aa.responseTimeLabel': 'Tempo di risposta',
    'aa.trainingLogs': 'Registri di addestramento',
    'aa.trainingLogsTitle': "Registri di addestramento dell'IA",
    'aa.trainingLogsDesc': 'Sessioni di addestramento recenti e aggiornamenti del modello',
    'aa.noTrainingLogs': 'Nessun registro di addestramento',
    'aa.trainingHistoryUpcoming': 'La cronologia di addestramento apparirà qui in una prossima versione.',
    'aa.loadingAnalytics': 'Caricamento delle analisi',
    'aa.loadingAnalyticsDesc': "Generazione di analisi complete delle prestazioni dell'IA...",
    'aa.loadingTrainingLogs': 'Caricamento dei registri di addestramento',
    'aa.loadingTrainingLogsDesc': "Recupero della cronologia di addestramento e dei registri delle prestazioni dell'IA...",
    'aa.loadingCaseDetails': 'Caricamento dei dettagli del caso',
    'aa.loadingCaseDetailsDesc': 'Recupero dei dettagli del caso n. {id}',
    'aa.resolutionPerformance': 'Prestazioni di risoluzione',
    'aa.learningProgress': "Progresso dell'apprendimento",
    'aa.knowledgeBaseEntries': 'Voci della base di conoscenza',
    'aa.successfulResolutions': 'Risoluzioni riuscite',
    'aa.totalConversations': 'Conversazioni totali',
    'aa.knowledgeBaseEntriesCount': 'Voci della base di conoscenza ({count})',
    'aa.addEntry': 'Aggiungi voce',
    'aa.noKnowledgeEntries': 'Nessuna voce nella base di conoscenza',
    'aa.addFirstEntry': "Aggiungi la tua prima voce alla base di conoscenza per iniziare ad addestrare l'IA",
    'aa.createFirstEntry': 'Crea la prima voce',
    'aa.uses': '{count} utilizzi',
    'aa.recentFeedback': 'Feedback recenti',
    'aa.noFeedback': 'Nessun feedback',
    'aa.feedbackWillAppear': "Il feedback dei clienti sulle risoluzioni dell'IA apparirà qui.",
    'aa.learningOpportunities': 'Opportunità di apprendimento',
    'aa.improveResolutionRate': 'Migliora il tasso di risoluzione',
    'aa.improveResolutionDesc': 'Esamina le conversazioni inoltrate per individuare le lacune che puoi colmare con nuove voci nella base di conoscenza.',
    'aa.reviewCases': 'Esamina i casi',
    'aa.patternDetection': 'Rilevamento di pattern',
    'aa.patternDetectionDesc': 'Il rilevamento automatico di tendenze e pattern non è ancora disponibile.',
    'aa.updateTrainings': 'Aggiorna addestramenti',
    'aa.addKnowledgeTitle': 'Aggiungi voce di conoscenza',
    'aa.addKnowledgeDesc': "Aggiungi nuove informazioni alla base di conoscenza dell'IA",
    'aa.titleRequired': 'Titolo *',
    'aa.knowledgeTitlePlaceholder': 'Titolo della voce di conoscenza',
    'aa.contentRequired': 'Contenuto *',
    'aa.contentPlaceholder': 'Contenuto dettagliato o soluzione',
    'aa.technical': 'Tecnico',
    'aa.billing': 'Fatturazione',
    'aa.general': 'Generale',
    'aa.policy': 'Politica',
    'aa.priority': 'Priorità',
    'aa.low': 'Bassa',
    'aa.high': 'Alta',
    'aa.tags': 'Tag',
    'aa.tagsPlaceholder': 'Tag separati da virgole',
    'aa.addToKnowledgeBase': 'Aggiungi alla base di conoscenza',
    'aa.importTitle': 'Importa base di conoscenza',
    'aa.importDesc': 'Importa voci di conoscenza da un file CSV o JSON',
    'aa.selectFile': 'Seleziona file',
    'aa.supportedFormats': 'Formati supportati: CSV, JSON. Dimensione massima del file: 10 MB',
    'aa.exportTitle': 'Esporta base di conoscenza',
    'aa.exportDesc': 'Scegli il formato per esportare la tua base di conoscenza',
    'aa.csvFormat': 'Formato CSV',
    'aa.csvFormatDesc': 'Compatibile con fogli di calcolo',
    'aa.jsonFormat': 'Formato JSON',
    'aa.jsonFormatDesc': 'Dati strutturati',
    'aa.reviewEscalatedTitle': 'Esamina i casi inoltrati',
    'aa.reviewEscalatedDesc': "Esamina e analizza i casi che sono stati inoltrati dall'IA agli agenti umani",
    'aa.recentEscalatedCases': 'Casi inoltrati recenti',
    'aa.noEscalatedCases': 'Nessun caso inoltrato da esaminare',
    'aa.escalatedWillAppear': 'Le conversazioni inoltrate appariranno qui in una prossima versione.',
    'aa.aiConfidenceLabel': "Confidenza dell'IA:",
    'aa.escalationAnalysis': 'Analisi delle escalation',
    'aa.escalationAnalysisUnavailable': "L'analisi delle escalation non è ancora disponibile.",
    'aa.generateAnalysisReport': 'Genera report di analisi',
    'aa.analysisReportUnavailable': 'La generazione di report di analisi sarà disponibile in una prossima versione.',
    'aa.caseDetailsTitle': 'Dettagli del caso - n. {id}',
    'aa.caseDetailsDesc': 'Informazioni dettagliate sul caso inoltrato',
    'aa.caseId': 'ID del caso',
    'aa.customer': 'Cliente',
    'aa.unknownCustomer': 'Cliente sconosciuto',
    'aa.escalated': 'Inoltrato',
    'aa.unknownTime': 'Ora sconosciuta',
    'aa.issueDescription': 'Descrizione del problema',
    'aa.noIssueDescription': 'Nessuna descrizione del problema disponibile',
    'aa.aiConfidenceLevel': "Livello di confidenza dell'IA",
    'aa.escalationReason': "Motivo dell'inoltro",
    'aa.escalationReasonText': "Il livello di confidenza dell'IA era inferiore alla soglia. Problema tecnico complesso che richiede competenza umana.",
    'aa.provideFeedbackCaseTitle': 'Fornisci feedback - Caso n. {id}',
    'aa.provideFeedbackDesc': "Il tuo feedback aiuta a migliorare le prestazioni dell'IA per casi simili",
    'aa.caseSummary': 'Riepilogo del caso',
    'aa.feedbackCustomer': 'Cliente: {name}',
    'aa.feedbackIssue': 'Problema: {issue}',
    'aa.yourFeedback': 'Il tuo feedback *',
    'aa.feedbackPlaceholder': "Come avrebbe potuto l'IA gestire meglio questo caso? Quale addestramento o conoscenza aggiuntiva sarebbe utile?",
    'aa.feedbackGuidelines': 'Linee guida per il feedback:',
    'aa.feedbackGuideline1': "Descrivi cosa l'IA ha tralasciato o frainteso",
    'aa.feedbackGuideline2': 'Suggerisci miglioramenti per la base di conoscenza',
    'aa.feedbackGuideline3': 'Segnala eventuali problemi di comunicazione con il cliente',
    'aa.submitFeedback': 'Invia feedback',
    'aa.trainingFillScenario': 'Compila lo scenario e la risposta prevista',
    'aa.trainingUnavailable': "L'addestramento del modello di IA sarà disponibile in una prossima versione.",
    'aa.fillTitleContent': 'Compila il titolo e il contenuto',
    'aa.knowledgeAdded': 'Conoscenza aggiunta',
    'aa.knowledgeAddedDesc': 'Una nuova voce è stata aggiunta alla base di conoscenza',
    'aa.addKnowledgeError': 'Impossibile aggiungere la voce alla base di conoscenza',
    'aa.importUnavailable': "L'importazione della base di conoscenza sarà disponibile in una prossima versione.",
    'aa.nothingToExport': 'Niente da esportare',
    'aa.nothingToExportDesc': 'Non ci sono ancora voci nella base di conoscenza da esportare.',
    'aa.exportComplete': 'Esportazione completata',
    'aa.exportCompleteDesc': 'Base di conoscenza esportata come {format}.',
    'aa.trainingUpdatesUnavailable': "Gli aggiornamenti di addestramento dell'IA saranno disponibili in una prossima versione.",
    'aa.feedbackRequired': 'Feedback obbligatorio',
    'aa.feedbackRequiredDesc': "Fornisci un feedback prima dell'invio.",
    'aa.feedbackSubmitted': 'Feedback inviato',
    'aa.feedbackSubmittedDesc': "Il feedback per il caso n. {id} è stato registrato e contribuirà a migliorare le prestazioni dell'IA.",
    'aa.saveConfigError': 'Impossibile salvare la configurazione IA',
    'aa.loadingRules': 'Caricamento delle regole di risoluzione automatica...',
    'aa.autoResolutionMgmt': 'Gestione della risoluzione automatica',
    'aa.autoResolutionSubtitle': 'Configura regole di risposta intelligenti e soglie',
    'aa.activeRulesBadge': '{count} regole attive',
    'aa.aiSettings': 'Impostazioni IA',
    'aa.aiModel': 'Modello di IA',
    'aa.advancedReasoning': 'Ragionamento avanzato e assistenza clienti intelligente',
    'aa.confidenceThresholdHint': 'Valori più alti = risposte più prudenti (0.1-1.0)',
    'aa.advancedFeatures': 'Funzionalità avanzate',
    'aa.activeLearningFromInteractions': 'Apprendimento attivo dalle interazioni con i clienti',
    'aa.activeRules': 'Regole attive',
    'aa.avgSuccessRate': 'Tasso medio di successo',
    'aa.totalUsage': 'Utilizzo totale',
    'aa.advancedAi': 'IA avanzata',
    'aa.tab.rules': 'Regole di risoluzione',
    'aa.tab.create': 'Crea regola',
    'aa.autoResolutionRulesCount': 'Regole di risoluzione automatica ({count})',
    'aa.keywordsLabel': 'Parole chiave: {keywords}',
    'aa.confidenceLabel': 'Confidenza: {value}%',
    'aa.usedLabel': 'Usata: {count} volte',
    'aa.successLabel': 'Successo: {value}%',
    'aa.createNewRule': 'Crea nuova regola di risoluzione automatica',
    'aa.ruleName': 'Nome della regola *',
    'aa.ruleNamePlaceholder': 'Inserisci il nome della regola',
    'aa.description': 'Descrizione',
    'aa.descriptionPlaceholder': 'Descrivi cosa gestisce questa regola',
    'aa.keywordsCommaSeparated': 'Parole chiave (separate da virgole)',
    'aa.keywordsPlaceholder': 'password, reimposta, dimenticata, accesso',
    'aa.autoResponse': 'Risposta automatica *',
    'aa.autoResponsePlaceholder': 'Inserisci la risposta automatica per questa regola',
    'aa.enableRuleImmediately': 'Abilita immediatamente questa regola',
    'aa.saveRule': 'Salva regola',
    'aa.responseLabel': 'Risposta:',
    'aa.aiConfigSettings': 'Impostazioni di configurazione IA',
    'aa.aiEngine': 'Motore IA',
    'aa.poweredBy': 'Basato su {provider} ({model})',
    'aa.resolutionAccuracy': 'Precisione di risoluzione',
    'aa.avgResponseTime': 'Tempo medio di risposta',
    'aa.configureAiParams': 'Configura parametri IA',
    'aa.editRuleTitle': 'Modifica regola di risoluzione automatica',
    'aa.editRuleDesc': 'Modifica le impostazioni della regola di risoluzione automatica',
    'aa.ruleIsEnabled': 'La regola è abilitata',
    'aa.updateRule': 'Aggiorna regola',
    'aa.areYouSure': 'Sei sicuro?',
    'aa.deleteConfirmPre': 'Questa azione non può essere annullata. Eliminerà definitivamente la regola di risoluzione automatica',
    'aa.deleteConfirmPost': 'e rimuoverà tutti i dati associati.',
    'aa.deleteRule': 'Elimina regola',
    'aa.fillRuleNameResponse': 'Compila il nome della regola e la risposta',
    'aa.ruleCreated': 'Regola creata',
    'aa.ruleCreatedDesc': 'La regola di risoluzione automatica è stata creata correttamente',
    'aa.createRuleError': 'Impossibile creare la regola di risoluzione automatica',
    'aa.ruleUpdated': 'Regola aggiornata',
    'aa.ruleUpdatedDesc': 'La regola di risoluzione automatica è stata aggiornata correttamente',
    'aa.updateRuleError': 'Impossibile aggiornare la regola di risoluzione automatica',
    'aa.ruleEnabledSuccess': 'Regola abilitata correttamente',
    'aa.ruleDisabledSuccess': 'Regola disabilitata correttamente',
    'aa.ruleDeleted': 'Regola eliminata',
    'aa.ruleDeletedDesc': 'La regola di risoluzione automatica è stata eliminata correttamente',
    'aa.deleteRuleError': 'Impossibile eliminare la regola di risoluzione automatica',
    'aa.loadRulesError': 'Impossibile caricare le regole di risoluzione automatica',
    'aa.updateConfigError': 'Impossibile aggiornare la configurazione IA',
  },
  ja: {
    'aa.configTitle': 'AIエージェント設定',
    'aa.configDesc': 'AIの動作、しきい値、運用パラメーターを設定します',
    'aa.confidenceThreshold': '信頼度しきい値',
    'aa.maxResponseTime': '最大応答時間（秒）',
    'aa.saveConfiguration': '設定を保存',
    'aa.autoEscalation': '複雑な問題の自動エスカレーション',
    'aa.missingInfo': '情報が不足しています',
    'aa.aiConfigUpdated': 'AI設定を更新しました',
    'aa.aiConfigSaved': 'AIエージェントの設定が正常に保存されました',
    'aa.notAvailableYet': 'まだ利用できません',
    'aa.viewDetails': '詳細を表示',
    'aa.provideFeedback': 'フィードバックを送信',
    'aa.import': 'インポート',
    'aa.export': 'エクスポート',
    'aa.active': 'アクティブ',
    'aa.disabled': '無効',
    'aa.dashboardTitle': 'AIエージェントダッシュボード',
    'aa.dashboardSubtitle': 'インテリジェントな一次解決システム',
    'aa.managedBy': '| 管理者: {name}',
    'aa.configure': '設定',
    'aa.confThreshold70': '70% - 応答は多いが精度は低い',
    'aa.confThreshold80': '80% - バランス型',
    'aa.confThreshold85': '85% - 推奨設定',
    'aa.confThreshold90': '90% - 高精度、応答は少ない',
    'aa.confThreshold95': '95% - 最高精度',
    'aa.learningMode': '学習モード',
    'aa.activeLearning': 'アクティブ学習',
    'aa.passiveLearning': 'パッシブ学習',
    'aa.supervisedOnly': '教師あり学習のみ',
    'aa.features': '機能',
    'aa.trainAI': 'AIをトレーニング',
    'aa.trainingSessionTitle': 'AIトレーニングセッション',
    'aa.trainingSessionDesc': 'AIの応答を改善するために新しいトレーニングシナリオを追加します',
    'aa.customerScenario': '顧客シナリオ *',
    'aa.scenarioPlaceholder': '顧客の問題や質問を記述してください...',
    'aa.expectedResponse': '期待されるAIの応答 *',
    'aa.expectedResponsePlaceholder': 'AIが提供すべき理想的な応答...',
    'aa.difficultyLevel': '難易度レベル',
    'aa.easy': '簡単',
    'aa.medium': '中程度',
    'aa.hard': '難しい',
    'aa.expert': 'エキスパート',
    'aa.category': 'カテゴリー',
    'aa.generalSupport': '一般サポート',
    'aa.technicalIssues': '技術的な問題',
    'aa.billingQuestions': '請求に関する質問',
    'aa.productInformation': '製品情報',
    'aa.trainAIModel': 'AIモデルをトレーニング',
    'aa.currentLoad': '現在の負荷',
    'aa.activeAiConversations': 'アクティブなAI会話',
    'aa.avgResponse': '平均応答',
    'aa.responseTime': '応答時間',
    'aa.confidenceScore': '信頼度スコア',
    'aa.avgConfidence': '平均信頼度',
    'aa.queueLength': 'キューの長さ',
    'aa.notAvailableYetLower': 'まだ利用できません',
    'aa.tab.conversations': 'アクティブな会話',
    'aa.tab.analytics': 'パフォーマンス分析',
    'aa.tab.knowledge': 'ナレッジベース',
    'aa.tab.learning': '学習とフィードバック',
    'aa.realtimeConversations': 'リアルタイム会話 ({count})',
    'aa.refreshConversations': '会話を更新',
    'aa.loadingConversations': '会話を読み込んでいます...',
    'aa.noActiveConversations': 'アクティブなAI会話はありません',
    'aa.aiHandlesAutomatically': 'AIが新しい会話を自動的に処理します',
    'aa.customerInquiry': '顧客からの問い合わせ',
    'aa.processing': '処理中...',
    'aa.justNow': 'たった今',
    'aa.convDetailUnavailable': '会話の詳細表示は今後のリリースで利用可能になります。',
    'aa.escalate': 'エスカレーション',
    'aa.escalationUnavailable': 'ダッシュボード内のエスカレーションは今後のリリースで利用可能になります。',
    'aa.aiStatus': 'AIステータス',
    'aa.telemetryUnavailable': 'ライブシステムテレメトリはまだ利用できません。',
    'aa.quickActions': 'クイックアクション',
    'aa.addKnowledge': 'ナレッジを追加',
    'aa.viewAnalytics': '分析を表示',
    'aa.analyticsTitle': 'AIパフォーマンス分析',
    'aa.analyticsDesc': 'AIエージェントの包括的なパフォーマンス指標とインサイト',
    'aa.autoResolutionRate': '自動解決率',
    'aa.conversations': '会話',
    'aa.escalationRate': 'エスカレーション率',
    'aa.performanceBreakdown': 'パフォーマンスの内訳',
    'aa.resolutionRate': '解決率',
    'aa.customerSatisfaction': '顧客満足度',
    'aa.responseTimeLabel': '応答時間',
    'aa.trainingLogs': 'トレーニングログ',
    'aa.trainingLogsTitle': 'AIトレーニングログ',
    'aa.trainingLogsDesc': '最近のトレーニングセッションとモデルの更新',
    'aa.noTrainingLogs': 'トレーニングログはまだありません',
    'aa.trainingHistoryUpcoming': 'トレーニング履歴は今後のリリースでここに表示されます。',
    'aa.loadingAnalytics': '分析を読み込んでいます',
    'aa.loadingAnalyticsDesc': '包括的なAIパフォーマンス分析を生成しています...',
    'aa.loadingTrainingLogs': 'トレーニングログを読み込んでいます',
    'aa.loadingTrainingLogsDesc': 'AIトレーニング履歴とパフォーマンスログを取得しています...',
    'aa.loadingCaseDetails': 'ケースの詳細を読み込んでいます',
    'aa.loadingCaseDetailsDesc': 'ケース #{id} の詳細を取得しています',
    'aa.resolutionPerformance': '解決パフォーマンス',
    'aa.learningProgress': '学習の進捗',
    'aa.knowledgeBaseEntries': 'ナレッジベースのエントリ',
    'aa.successfulResolutions': '解決成功数',
    'aa.totalConversations': '会話の総数',
    'aa.knowledgeBaseEntriesCount': 'ナレッジベースのエントリ ({count})',
    'aa.addEntry': 'エントリを追加',
    'aa.noKnowledgeEntries': 'ナレッジベースのエントリがありません',
    'aa.addFirstEntry': 'AIのトレーニングを開始するには、最初のナレッジベースエントリを追加してください',
    'aa.createFirstEntry': '最初のエントリを作成',
    'aa.uses': '{count} 回使用',
    'aa.recentFeedback': '最近のフィードバック',
    'aa.noFeedback': 'フィードバックはまだありません',
    'aa.feedbackWillAppear': 'AIの解決に関する顧客のフィードバックがここに表示されます。',
    'aa.learningOpportunities': '学習の機会',
    'aa.improveResolutionRate': '解決率を改善',
    'aa.improveResolutionDesc': 'エスカレーションされた会話を確認し、新しいナレッジベースエントリで埋められるギャップを見つけてください。',
    'aa.reviewCases': 'ケースを確認',
    'aa.patternDetection': 'パターン検出',
    'aa.patternDetectionDesc': 'トレンドとパターンの自動検出はまだ利用できません。',
    'aa.updateTrainings': 'トレーニングを更新',
    'aa.addKnowledgeTitle': 'ナレッジエントリを追加',
    'aa.addKnowledgeDesc': 'AIナレッジベースに新しい情報を追加します',
    'aa.titleRequired': 'タイトル *',
    'aa.knowledgeTitlePlaceholder': 'ナレッジエントリのタイトル',
    'aa.contentRequired': '内容 *',
    'aa.contentPlaceholder': '詳細な内容または解決策',
    'aa.technical': '技術',
    'aa.billing': '請求',
    'aa.general': '一般',
    'aa.policy': 'ポリシー',
    'aa.priority': '優先度',
    'aa.low': '低',
    'aa.high': '高',
    'aa.tags': 'タグ',
    'aa.tagsPlaceholder': 'カンマで区切ったタグ',
    'aa.addToKnowledgeBase': 'ナレッジベースに追加',
    'aa.importTitle': 'ナレッジベースをインポート',
    'aa.importDesc': 'CSVまたはJSONファイルからナレッジエントリをインポートします',
    'aa.selectFile': 'ファイルを選択',
    'aa.supportedFormats': '対応形式: CSV、JSON。最大ファイルサイズ: 10MB',
    'aa.exportTitle': 'ナレッジベースをエクスポート',
    'aa.exportDesc': 'ナレッジベースをエクスポートする形式を選択してください',
    'aa.csvFormat': 'CSV形式',
    'aa.csvFormatDesc': '表計算ソフト対応',
    'aa.jsonFormat': 'JSON形式',
    'aa.jsonFormatDesc': '構造化データ',
    'aa.reviewEscalatedTitle': 'エスカレーションされたケースを確認',
    'aa.reviewEscalatedDesc': 'AIから人間のエージェントにエスカレーションされたケースを確認および分析します',
    'aa.recentEscalatedCases': '最近エスカレーションされたケース',
    'aa.noEscalatedCases': '確認すべきエスカレーションされたケースはまだありません',
    'aa.escalatedWillAppear': 'エスカレーションされた会話は今後のリリースでここに表示されます。',
    'aa.aiConfidenceLabel': 'AI信頼度:',
    'aa.escalationAnalysis': 'エスカレーション分析',
    'aa.escalationAnalysisUnavailable': 'エスカレーション分析はまだ利用できません。',
    'aa.generateAnalysisReport': '分析レポートを生成',
    'aa.analysisReportUnavailable': '分析レポートの生成は今後のリリースで利用可能になります。',
    'aa.caseDetailsTitle': 'ケースの詳細 - #{id}',
    'aa.caseDetailsDesc': 'エスカレーションされたケースの詳細情報',
    'aa.caseId': 'ケースID',
    'aa.customer': '顧客',
    'aa.unknownCustomer': '不明な顧客',
    'aa.escalated': 'エスカレーション済み',
    'aa.unknownTime': '不明な時刻',
    'aa.issueDescription': '問題の説明',
    'aa.noIssueDescription': '問題の説明はありません',
    'aa.aiConfidenceLevel': 'AI信頼度レベル',
    'aa.escalationReason': 'エスカレーションの理由',
    'aa.escalationReasonText': 'AIの信頼度レベルがしきい値を下回りました。人間の専門知識を必要とする複雑な技術的問題です。',
    'aa.provideFeedbackCaseTitle': 'フィードバックを送信 - ケース #{id}',
    'aa.provideFeedbackDesc': 'あなたのフィードバックは類似ケースでのAIパフォーマンスの改善に役立ちます',
    'aa.caseSummary': 'ケースの概要',
    'aa.feedbackCustomer': '顧客: {name}',
    'aa.feedbackIssue': '問題: {issue}',
    'aa.yourFeedback': 'あなたのフィードバック *',
    'aa.feedbackPlaceholder': 'AIはこのケースをどのように処理すればよかったでしょうか？どのような追加のトレーニングや知識が役立ちますか？',
    'aa.feedbackGuidelines': 'フィードバックのガイドライン:',
    'aa.feedbackGuideline1': 'AIが見逃した点や誤解した点を説明してください',
    'aa.feedbackGuideline2': 'ナレッジベースの改善を提案してください',
    'aa.feedbackGuideline3': '顧客とのコミュニケーションの問題があれば記録してください',
    'aa.submitFeedback': 'フィードバックを送信',
    'aa.trainingFillScenario': 'シナリオと期待される応答を入力してください',
    'aa.trainingUnavailable': 'AIモデルのトレーニングは今後のリリースで利用可能になります。',
    'aa.fillTitleContent': 'タイトルと内容を入力してください',
    'aa.knowledgeAdded': 'ナレッジを追加しました',
    'aa.knowledgeAddedDesc': '新しいエントリがナレッジベースに追加されました',
    'aa.addKnowledgeError': 'ナレッジベースエントリの追加に失敗しました',
    'aa.importUnavailable': 'ナレッジベースのインポートは今後のリリースで利用可能になります。',
    'aa.nothingToExport': 'エクスポートするものがありません',
    'aa.nothingToExportDesc': 'エクスポートできるナレッジベースエントリはまだありません。',
    'aa.exportComplete': 'エクスポート完了',
    'aa.exportCompleteDesc': 'ナレッジベースを {format} としてエクスポートしました。',
    'aa.trainingUpdatesUnavailable': 'AIトレーニングの更新は今後のリリースで利用可能になります。',
    'aa.feedbackRequired': 'フィードバックが必要です',
    'aa.feedbackRequiredDesc': '送信する前にフィードバックを入力してください。',
    'aa.feedbackSubmitted': 'フィードバックを送信しました',
    'aa.feedbackSubmittedDesc': 'ケース #{id} のフィードバックが記録され、AIパフォーマンスの改善に役立てられます。',
    'aa.saveConfigError': 'AI設定の保存に失敗しました',
    'aa.loadingRules': '自動解決ルールを読み込んでいます...',
    'aa.autoResolutionMgmt': '自動解決管理',
    'aa.autoResolutionSubtitle': 'インテリジェントな応答ルールとしきい値を設定します',
    'aa.activeRulesBadge': 'アクティブなルール {count} 件',
    'aa.aiSettings': 'AI設定',
    'aa.aiModel': 'AIモデル',
    'aa.advancedReasoning': '高度な推論とインテリジェントな顧客サポート',
    'aa.confidenceThresholdHint': '値が高いほど慎重な応答になります（0.1〜1.0）',
    'aa.advancedFeatures': '高度な機能',
    'aa.activeLearningFromInteractions': '顧客とのやり取りからのアクティブ学習',
    'aa.activeRules': 'アクティブなルール',
    'aa.avgSuccessRate': '平均成功率',
    'aa.totalUsage': '総使用量',
    'aa.advancedAi': '高度なAI',
    'aa.tab.rules': '解決ルール',
    'aa.tab.create': 'ルールを作成',
    'aa.autoResolutionRulesCount': '自動解決ルール ({count})',
    'aa.keywordsLabel': 'キーワード: {keywords}',
    'aa.confidenceLabel': '信頼度: {value}%',
    'aa.usedLabel': '使用回数: {count} 回',
    'aa.successLabel': '成功: {value}%',
    'aa.createNewRule': '新しい自動解決ルールを作成',
    'aa.ruleName': 'ルール名 *',
    'aa.ruleNamePlaceholder': 'ルール名を入力',
    'aa.description': '説明',
    'aa.descriptionPlaceholder': 'このルールが処理する内容を記述してください',
    'aa.keywordsCommaSeparated': 'キーワード（カンマ区切り）',
    'aa.keywordsPlaceholder': 'パスワード, リセット, 忘れた, ログイン',
    'aa.autoResponse': '自動応答 *',
    'aa.autoResponsePlaceholder': 'このルールの自動応答を入力してください',
    'aa.enableRuleImmediately': 'このルールをすぐに有効にする',
    'aa.saveRule': 'ルールを保存',
    'aa.responseLabel': '応答:',
    'aa.aiConfigSettings': 'AI構成設定',
    'aa.aiEngine': 'AIエンジン',
    'aa.poweredBy': '{provider} ({model}) を使用',
    'aa.resolutionAccuracy': '解決精度',
    'aa.avgResponseTime': '平均応答時間',
    'aa.configureAiParams': 'AIパラメーターを設定',
    'aa.editRuleTitle': '自動解決ルールを編集',
    'aa.editRuleDesc': '自動解決ルールの設定を変更します',
    'aa.ruleIsEnabled': 'ルールは有効です',
    'aa.updateRule': 'ルールを更新',
    'aa.areYouSure': 'よろしいですか？',
    'aa.deleteConfirmPre': 'この操作は元に戻せません。自動解決ルールを完全に削除します',
    'aa.deleteConfirmPost': '関連するすべてのデータを削除します。',
    'aa.deleteRule': 'ルールを削除',
    'aa.fillRuleNameResponse': 'ルール名と応答を入力してください',
    'aa.ruleCreated': 'ルールを作成しました',
    'aa.ruleCreatedDesc': '自動解決ルールが正常に作成されました',
    'aa.createRuleError': '自動解決ルールの作成に失敗しました',
    'aa.ruleUpdated': 'ルールを更新しました',
    'aa.ruleUpdatedDesc': '自動解決ルールが正常に更新されました',
    'aa.updateRuleError': '自動解決ルールの更新に失敗しました',
    'aa.ruleEnabledSuccess': 'ルールが正常に有効化されました',
    'aa.ruleDisabledSuccess': 'ルールが正常に無効化されました',
    'aa.ruleDeleted': 'ルールを削除しました',
    'aa.ruleDeletedDesc': '自動解決ルールが正常に削除されました',
    'aa.deleteRuleError': '自動解決ルールの削除に失敗しました',
    'aa.loadRulesError': '自動解決ルールの読み込みに失敗しました',
    'aa.updateConfigError': 'AI設定の更新に失敗しました',
  },
  ko: {
    'aa.configTitle': 'AI 에이전트 구성',
    'aa.configDesc': 'AI 동작, 임계값 및 운영 매개변수를 구성합니다',
    'aa.confidenceThreshold': '신뢰도 임계값',
    'aa.maxResponseTime': '최대 응답 시간(초)',
    'aa.saveConfiguration': '구성 저장',
    'aa.autoEscalation': '복잡한 문제에 대한 자동 에스컬레이션',
    'aa.missingInfo': '정보 누락',
    'aa.aiConfigUpdated': 'AI 구성이 업데이트됨',
    'aa.aiConfigSaved': 'AI 에이전트 설정이 성공적으로 저장되었습니다',
    'aa.notAvailableYet': '아직 사용할 수 없음',
    'aa.viewDetails': '세부 정보 보기',
    'aa.provideFeedback': '피드백 제공',
    'aa.import': '가져오기',
    'aa.export': '내보내기',
    'aa.active': '활성',
    'aa.disabled': '비활성화됨',
    'aa.dashboardTitle': 'AI 에이전트 대시보드',
    'aa.dashboardSubtitle': '지능형 일선 해결 시스템',
    'aa.managedBy': '| 관리자: {name}',
    'aa.configure': '구성',
    'aa.confThreshold70': '70% - 응답 많음, 정확도 낮음',
    'aa.confThreshold80': '80% - 균형 잡힌 접근',
    'aa.confThreshold85': '85% - 권장 설정',
    'aa.confThreshold90': '90% - 높은 정확도, 응답 적음',
    'aa.confThreshold95': '95% - 최대 정확도',
    'aa.learningMode': '학습 모드',
    'aa.activeLearning': '능동 학습',
    'aa.passiveLearning': '수동 학습',
    'aa.supervisedOnly': '지도 학습만',
    'aa.features': '기능',
    'aa.trainAI': 'AI 학습',
    'aa.trainingSessionTitle': 'AI 학습 세션',
    'aa.trainingSessionDesc': 'AI 응답을 개선하기 위해 새로운 학습 시나리오를 추가합니다',
    'aa.customerScenario': '고객 시나리오 *',
    'aa.scenarioPlaceholder': '고객의 문제나 질문을 설명하세요...',
    'aa.expectedResponse': '예상 AI 응답 *',
    'aa.expectedResponsePlaceholder': 'AI가 제공해야 할 이상적인 응답...',
    'aa.difficultyLevel': '난이도 수준',
    'aa.easy': '쉬움',
    'aa.medium': '보통',
    'aa.hard': '어려움',
    'aa.expert': '전문가',
    'aa.category': '카테고리',
    'aa.generalSupport': '일반 지원',
    'aa.technicalIssues': '기술적 문제',
    'aa.billingQuestions': '청구 관련 질문',
    'aa.productInformation': '제품 정보',
    'aa.trainAIModel': 'AI 모델 학습',
    'aa.currentLoad': '현재 부하',
    'aa.activeAiConversations': '활성 AI 대화',
    'aa.avgResponse': '평균 응답',
    'aa.responseTime': '응답 시간',
    'aa.confidenceScore': '신뢰도 점수',
    'aa.avgConfidence': '평균 신뢰도',
    'aa.queueLength': '대기열 길이',
    'aa.notAvailableYetLower': '아직 사용할 수 없음',
    'aa.tab.conversations': '활성 대화',
    'aa.tab.analytics': '성능 분석',
    'aa.tab.knowledge': '지식 베이스',
    'aa.tab.learning': '학습 및 피드백',
    'aa.realtimeConversations': '실시간 대화 ({count})',
    'aa.refreshConversations': '대화 새로 고침',
    'aa.loadingConversations': '대화를 불러오는 중...',
    'aa.noActiveConversations': '활성 AI 대화가 없습니다',
    'aa.aiHandlesAutomatically': 'AI가 새로운 대화를 자동으로 처리합니다',
    'aa.customerInquiry': '고객 문의',
    'aa.processing': '처리 중...',
    'aa.justNow': '방금 전',
    'aa.convDetailUnavailable': '대화 세부 정보 보기는 향후 릴리스에서 제공될 예정입니다.',
    'aa.escalate': '에스컬레이션',
    'aa.escalationUnavailable': '대시보드 내 에스컬레이션은 향후 릴리스에서 제공될 예정입니다.',
    'aa.aiStatus': 'AI 상태',
    'aa.telemetryUnavailable': '실시간 시스템 원격 측정은 아직 사용할 수 없습니다.',
    'aa.quickActions': '빠른 작업',
    'aa.addKnowledge': '지식 추가',
    'aa.viewAnalytics': '분석 보기',
    'aa.analyticsTitle': 'AI 성능 분석',
    'aa.analyticsDesc': 'AI 에이전트의 종합적인 성능 지표 및 인사이트',
    'aa.autoResolutionRate': '자동 해결률',
    'aa.conversations': '대화',
    'aa.escalationRate': '에스컬레이션 비율',
    'aa.performanceBreakdown': '성능 세부 내역',
    'aa.resolutionRate': '해결률',
    'aa.customerSatisfaction': '고객 만족도',
    'aa.responseTimeLabel': '응답 시간',
    'aa.trainingLogs': '학습 로그',
    'aa.trainingLogsTitle': 'AI 학습 로그',
    'aa.trainingLogsDesc': '최근 학습 세션 및 모델 업데이트',
    'aa.noTrainingLogs': '아직 학습 로그가 없습니다',
    'aa.trainingHistoryUpcoming': '학습 기록은 향후 릴리스에서 여기에 표시됩니다.',
    'aa.loadingAnalytics': '분석을 불러오는 중',
    'aa.loadingAnalyticsDesc': '종합적인 AI 성능 분석을 생성하는 중...',
    'aa.loadingTrainingLogs': '학습 로그를 불러오는 중',
    'aa.loadingTrainingLogsDesc': 'AI 학습 기록 및 성능 로그를 가져오는 중...',
    'aa.loadingCaseDetails': '사례 세부 정보를 불러오는 중',
    'aa.loadingCaseDetailsDesc': '사례 #{id}의 세부 정보를 가져오는 중',
    'aa.resolutionPerformance': '해결 성능',
    'aa.learningProgress': '학습 진행 상황',
    'aa.knowledgeBaseEntries': '지식 베이스 항목',
    'aa.successfulResolutions': '성공적인 해결',
    'aa.totalConversations': '전체 대화',
    'aa.knowledgeBaseEntriesCount': '지식 베이스 항목 ({count})',
    'aa.addEntry': '항목 추가',
    'aa.noKnowledgeEntries': '지식 베이스 항목이 없습니다',
    'aa.addFirstEntry': 'AI 학습을 시작하려면 첫 번째 지식 베이스 항목을 추가하세요',
    'aa.createFirstEntry': '첫 항목 만들기',
    'aa.uses': '{count}회 사용',
    'aa.recentFeedback': '최근 피드백',
    'aa.noFeedback': '아직 피드백이 없습니다',
    'aa.feedbackWillAppear': 'AI 해결에 대한 고객 피드백이 여기에 표시됩니다.',
    'aa.learningOpportunities': '학습 기회',
    'aa.improveResolutionRate': '해결률 개선',
    'aa.improveResolutionDesc': '에스컬레이션된 대화를 검토하여 새로운 지식 베이스 항목으로 채울 수 있는 부족한 부분을 찾으세요.',
    'aa.reviewCases': '사례 검토',
    'aa.patternDetection': '패턴 감지',
    'aa.patternDetectionDesc': '트렌드 및 패턴 자동 감지는 아직 사용할 수 없습니다.',
    'aa.updateTrainings': '학습 업데이트',
    'aa.addKnowledgeTitle': '지식 항목 추가',
    'aa.addKnowledgeDesc': 'AI 지식 베이스에 새로운 정보를 추가합니다',
    'aa.titleRequired': '제목 *',
    'aa.knowledgeTitlePlaceholder': '지식 항목 제목',
    'aa.contentRequired': '내용 *',
    'aa.contentPlaceholder': '자세한 내용 또는 해결책',
    'aa.technical': '기술',
    'aa.billing': '청구',
    'aa.general': '일반',
    'aa.policy': '정책',
    'aa.priority': '우선순위',
    'aa.low': '낮음',
    'aa.high': '높음',
    'aa.tags': '태그',
    'aa.tagsPlaceholder': '쉼표로 구분된 태그',
    'aa.addToKnowledgeBase': '지식 베이스에 추가',
    'aa.importTitle': '지식 베이스 가져오기',
    'aa.importDesc': 'CSV 또는 JSON 파일에서 지식 항목을 가져옵니다',
    'aa.selectFile': '파일 선택',
    'aa.supportedFormats': '지원 형식: CSV, JSON. 최대 파일 크기: 10MB',
    'aa.exportTitle': '지식 베이스 내보내기',
    'aa.exportDesc': '지식 베이스를 내보낼 형식을 선택하세요',
    'aa.csvFormat': 'CSV 형식',
    'aa.csvFormatDesc': '스프레드시트 호환',
    'aa.jsonFormat': 'JSON 형식',
    'aa.jsonFormatDesc': '구조화된 데이터',
    'aa.reviewEscalatedTitle': '에스컬레이션된 사례 검토',
    'aa.reviewEscalatedDesc': 'AI에서 상담원으로 에스컬레이션된 사례를 검토하고 분석합니다',
    'aa.recentEscalatedCases': '최근 에스컬레이션된 사례',
    'aa.noEscalatedCases': '아직 검토할 에스컬레이션된 사례가 없습니다',
    'aa.escalatedWillAppear': '에스컬레이션된 대화는 향후 릴리스에서 여기에 표시됩니다.',
    'aa.aiConfidenceLabel': 'AI 신뢰도:',
    'aa.escalationAnalysis': '에스컬레이션 분석',
    'aa.escalationAnalysisUnavailable': '에스컬레이션 분석은 아직 사용할 수 없습니다.',
    'aa.generateAnalysisReport': '분석 보고서 생성',
    'aa.analysisReportUnavailable': '분석 보고서 생성은 향후 릴리스에서 제공될 예정입니다.',
    'aa.caseDetailsTitle': '사례 세부 정보 - #{id}',
    'aa.caseDetailsDesc': '에스컬레이션된 사례에 대한 상세 정보',
    'aa.caseId': '사례 ID',
    'aa.customer': '고객',
    'aa.unknownCustomer': '알 수 없는 고객',
    'aa.escalated': '에스컬레이션됨',
    'aa.unknownTime': '알 수 없는 시간',
    'aa.issueDescription': '문제 설명',
    'aa.noIssueDescription': '사용 가능한 문제 설명이 없습니다',
    'aa.aiConfidenceLevel': 'AI 신뢰도 수준',
    'aa.escalationReason': '에스컬레이션 사유',
    'aa.escalationReasonText': 'AI 신뢰도 수준이 임계값보다 낮았습니다. 사람의 전문 지식이 필요한 복잡한 기술적 문제입니다.',
    'aa.provideFeedbackCaseTitle': '피드백 제공 - 사례 #{id}',
    'aa.provideFeedbackDesc': '귀하의 피드백은 유사한 사례에서 AI 성능을 개선하는 데 도움이 됩니다',
    'aa.caseSummary': '사례 요약',
    'aa.feedbackCustomer': '고객: {name}',
    'aa.feedbackIssue': '문제: {issue}',
    'aa.yourFeedback': '귀하의 피드백 *',
    'aa.feedbackPlaceholder': 'AI가 이 사례를 어떻게 더 잘 처리할 수 있었을까요? 어떤 추가 학습이나 지식이 도움이 될까요?',
    'aa.feedbackGuidelines': '피드백 지침:',
    'aa.feedbackGuideline1': 'AI가 놓쳤거나 잘못 이해한 부분을 설명하세요',
    'aa.feedbackGuideline2': '지식 베이스 개선 사항을 제안하세요',
    'aa.feedbackGuideline3': '고객 커뮤니케이션 문제가 있으면 기록하세요',
    'aa.submitFeedback': '피드백 제출',
    'aa.trainingFillScenario': '시나리오와 예상 응답을 입력하세요',
    'aa.trainingUnavailable': 'AI 모델 학습은 향후 릴리스에서 제공될 예정입니다.',
    'aa.fillTitleContent': '제목과 내용을 입력하세요',
    'aa.knowledgeAdded': '지식 추가됨',
    'aa.knowledgeAddedDesc': '새 항목이 지식 베이스에 추가되었습니다',
    'aa.addKnowledgeError': '지식 베이스 항목을 추가하지 못했습니다',
    'aa.importUnavailable': '지식 베이스 가져오기는 향후 릴리스에서 제공될 예정입니다.',
    'aa.nothingToExport': '내보낼 항목이 없습니다',
    'aa.nothingToExportDesc': '아직 내보낼 지식 베이스 항목이 없습니다.',
    'aa.exportComplete': '내보내기 완료',
    'aa.exportCompleteDesc': '지식 베이스를 {format}(으)로 내보냈습니다.',
    'aa.trainingUpdatesUnavailable': 'AI 학습 업데이트는 향후 릴리스에서 제공될 예정입니다.',
    'aa.feedbackRequired': '피드백 필요',
    'aa.feedbackRequiredDesc': '제출하기 전에 피드백을 제공하세요.',
    'aa.feedbackSubmitted': '피드백 제출됨',
    'aa.feedbackSubmittedDesc': '사례 #{id}에 대한 피드백이 기록되어 AI 성능 개선에 도움이 됩니다.',
    'aa.saveConfigError': 'AI 구성을 저장하지 못했습니다',
    'aa.loadingRules': '자동 해결 규칙을 불러오는 중...',
    'aa.autoResolutionMgmt': '자동 해결 관리',
    'aa.autoResolutionSubtitle': '지능형 응답 규칙과 임계값을 구성합니다',
    'aa.activeRulesBadge': '활성 규칙 {count}개',
    'aa.aiSettings': 'AI 설정',
    'aa.aiModel': 'AI 모델',
    'aa.advancedReasoning': '고급 추론 및 지능형 고객 지원',
    'aa.confidenceThresholdHint': '값이 높을수록 더 신중한 응답 (0.1-1.0)',
    'aa.advancedFeatures': '고급 기능',
    'aa.activeLearningFromInteractions': '고객 상호작용을 통한 능동 학습',
    'aa.activeRules': '활성 규칙',
    'aa.avgSuccessRate': '평균 성공률',
    'aa.totalUsage': '총 사용량',
    'aa.advancedAi': '고급 AI',
    'aa.tab.rules': '해결 규칙',
    'aa.tab.create': '규칙 만들기',
    'aa.autoResolutionRulesCount': '자동 해결 규칙 ({count})',
    'aa.keywordsLabel': '키워드: {keywords}',
    'aa.confidenceLabel': '신뢰도: {value}%',
    'aa.usedLabel': '사용: {count}회',
    'aa.successLabel': '성공: {value}%',
    'aa.createNewRule': '새 자동 해결 규칙 만들기',
    'aa.ruleName': '규칙 이름 *',
    'aa.ruleNamePlaceholder': '규칙 이름 입력',
    'aa.description': '설명',
    'aa.descriptionPlaceholder': '이 규칙이 처리하는 내용을 설명하세요',
    'aa.keywordsCommaSeparated': '키워드(쉼표로 구분)',
    'aa.keywordsPlaceholder': '비밀번호, 재설정, 잊음, 로그인',
    'aa.autoResponse': '자동 응답 *',
    'aa.autoResponsePlaceholder': '이 규칙에 대한 자동 응답을 입력하세요',
    'aa.enableRuleImmediately': '이 규칙을 즉시 활성화',
    'aa.saveRule': '규칙 저장',
    'aa.responseLabel': '응답:',
    'aa.aiConfigSettings': 'AI 구성 설정',
    'aa.aiEngine': 'AI 엔진',
    'aa.poweredBy': '{provider} ({model}) 기반',
    'aa.resolutionAccuracy': '해결 정확도',
    'aa.avgResponseTime': '평균 응답 시간',
    'aa.configureAiParams': 'AI 매개변수 구성',
    'aa.editRuleTitle': '자동 해결 규칙 편집',
    'aa.editRuleDesc': '자동 해결 규칙 설정을 수정합니다',
    'aa.ruleIsEnabled': '규칙이 활성화됨',
    'aa.updateRule': '규칙 업데이트',
    'aa.areYouSure': '확실합니까?',
    'aa.deleteConfirmPre': '이 작업은 취소할 수 없습니다. 자동 해결 규칙이 영구적으로 삭제됩니다',
    'aa.deleteConfirmPost': '관련된 모든 데이터가 제거됩니다.',
    'aa.deleteRule': '규칙 삭제',
    'aa.fillRuleNameResponse': '규칙 이름과 응답을 입력하세요',
    'aa.ruleCreated': '규칙 생성됨',
    'aa.ruleCreatedDesc': '자동 해결 규칙이 성공적으로 생성되었습니다',
    'aa.createRuleError': '자동 해결 규칙을 생성하지 못했습니다',
    'aa.ruleUpdated': '규칙 업데이트됨',
    'aa.ruleUpdatedDesc': '자동 해결 규칙이 성공적으로 업데이트되었습니다',
    'aa.updateRuleError': '자동 해결 규칙을 업데이트하지 못했습니다',
    'aa.ruleEnabledSuccess': '규칙이 성공적으로 활성화되었습니다',
    'aa.ruleDisabledSuccess': '규칙이 성공적으로 비활성화되었습니다',
    'aa.ruleDeleted': '규칙 삭제됨',
    'aa.ruleDeletedDesc': '자동 해결 규칙이 성공적으로 삭제되었습니다',
    'aa.deleteRuleError': '자동 해결 규칙을 삭제하지 못했습니다',
    'aa.loadRulesError': '자동 해결 규칙을 불러오지 못했습니다',
    'aa.updateConfigError': 'AI 구성을 업데이트하지 못했습니다',
  },
  zh: {
    'aa.configTitle': 'AI 智能体配置',
    'aa.configDesc': '配置 AI 行为、阈值和运行参数',
    'aa.confidenceThreshold': '置信度阈值',
    'aa.maxResponseTime': '最大响应时间（秒）',
    'aa.saveConfiguration': '保存配置',
    'aa.autoEscalation': '复杂问题自动升级',
    'aa.missingInfo': '信息不完整',
    'aa.aiConfigUpdated': 'AI 配置已更新',
    'aa.aiConfigSaved': 'AI 智能体设置已成功保存',
    'aa.notAvailableYet': '尚不可用',
    'aa.viewDetails': '查看详情',
    'aa.provideFeedback': '提供反馈',
    'aa.import': '导入',
    'aa.export': '导出',
    'aa.active': '活动',
    'aa.disabled': '已禁用',
    'aa.dashboardTitle': 'AI 智能体仪表板',
    'aa.dashboardSubtitle': '智能一线解决系统',
    'aa.managedBy': '| 管理者：{name}',
    'aa.configure': '配置',
    'aa.confThreshold70': '70% - 响应更多，准确度较低',
    'aa.confThreshold80': '80% - 平衡方式',
    'aa.confThreshold85': '85% - 推荐设置',
    'aa.confThreshold90': '90% - 高准确度，响应较少',
    'aa.confThreshold95': '95% - 最高准确度',
    'aa.learningMode': '学习模式',
    'aa.activeLearning': '主动学习',
    'aa.passiveLearning': '被动学习',
    'aa.supervisedOnly': '仅监督式',
    'aa.features': '功能',
    'aa.trainAI': '训练 AI',
    'aa.trainingSessionTitle': 'AI 训练会话',
    'aa.trainingSessionDesc': '添加新的训练场景以改进 AI 响应',
    'aa.customerScenario': '客户场景 *',
    'aa.scenarioPlaceholder': '描述客户的问题或疑问...',
    'aa.expectedResponse': '预期的 AI 响应 *',
    'aa.expectedResponsePlaceholder': 'AI 应提供的理想响应...',
    'aa.difficultyLevel': '难度级别',
    'aa.easy': '简单',
    'aa.medium': '中等',
    'aa.hard': '困难',
    'aa.expert': '专家',
    'aa.category': '类别',
    'aa.generalSupport': '一般支持',
    'aa.technicalIssues': '技术问题',
    'aa.billingQuestions': '账单问题',
    'aa.productInformation': '产品信息',
    'aa.trainAIModel': '训练 AI 模型',
    'aa.currentLoad': '当前负载',
    'aa.activeAiConversations': '活动的 AI 对话',
    'aa.avgResponse': '平均响应',
    'aa.responseTime': '响应时间',
    'aa.confidenceScore': '置信度评分',
    'aa.avgConfidence': '平均置信度',
    'aa.queueLength': '队列长度',
    'aa.notAvailableYetLower': '尚不可用',
    'aa.tab.conversations': '活动对话',
    'aa.tab.analytics': '性能分析',
    'aa.tab.knowledge': '知识库',
    'aa.tab.learning': '学习与反馈',
    'aa.realtimeConversations': '实时对话（{count}）',
    'aa.refreshConversations': '刷新对话',
    'aa.loadingConversations': '正在加载对话...',
    'aa.noActiveConversations': '没有活动的 AI 对话',
    'aa.aiHandlesAutomatically': 'AI 将自动处理新对话',
    'aa.customerInquiry': '客户咨询',
    'aa.processing': '处理中...',
    'aa.justNow': '刚刚',
    'aa.convDetailUnavailable': '对话详情视图将在即将发布的版本中提供。',
    'aa.escalate': '升级',
    'aa.escalationUnavailable': '仪表板内升级将在即将发布的版本中提供。',
    'aa.aiStatus': 'AI 状态',
    'aa.telemetryUnavailable': '实时系统遥测数据尚不可用。',
    'aa.quickActions': '快速操作',
    'aa.addKnowledge': '添加知识',
    'aa.viewAnalytics': '查看分析',
    'aa.analyticsTitle': 'AI 性能分析',
    'aa.analyticsDesc': '全面的 AI 智能体性能指标和洞察',
    'aa.autoResolutionRate': '自动解决率',
    'aa.conversations': '对话',
    'aa.escalationRate': '升级率',
    'aa.performanceBreakdown': '性能明细',
    'aa.resolutionRate': '解决率',
    'aa.customerSatisfaction': '客户满意度',
    'aa.responseTimeLabel': '响应时间',
    'aa.trainingLogs': '训练日志',
    'aa.trainingLogsTitle': 'AI 训练日志',
    'aa.trainingLogsDesc': '最近的训练会话和模型更新',
    'aa.noTrainingLogs': '尚无训练日志',
    'aa.trainingHistoryUpcoming': '训练历史将在即将发布的版本中显示在此处。',
    'aa.loadingAnalytics': '正在加载分析',
    'aa.loadingAnalyticsDesc': '正在生成全面的 AI 性能分析...',
    'aa.loadingTrainingLogs': '正在加载训练日志',
    'aa.loadingTrainingLogsDesc': '正在检索 AI 训练历史和性能日志...',
    'aa.loadingCaseDetails': '正在加载案例详情',
    'aa.loadingCaseDetailsDesc': '正在检索案例 #{id} 的详情',
    'aa.resolutionPerformance': '解决性能',
    'aa.learningProgress': '学习进度',
    'aa.knowledgeBaseEntries': '知识库条目',
    'aa.successfulResolutions': '成功解决',
    'aa.totalConversations': '总对话数',
    'aa.knowledgeBaseEntriesCount': '知识库条目（{count}）',
    'aa.addEntry': '添加条目',
    'aa.noKnowledgeEntries': '没有知识库条目',
    'aa.addFirstEntry': '添加您的第一个知识库条目以开始训练 AI',
    'aa.createFirstEntry': '创建第一个条目',
    'aa.uses': '{count} 次使用',
    'aa.recentFeedback': '最近的反馈',
    'aa.noFeedback': '尚无反馈',
    'aa.feedbackWillAppear': '客户对 AI 解决方案的反馈将显示在此处。',
    'aa.learningOpportunities': '学习机会',
    'aa.improveResolutionRate': '提高解决率',
    'aa.improveResolutionDesc': '查看升级的对话，找出可以通过新的知识库条目填补的空白。',
    'aa.reviewCases': '查看案例',
    'aa.patternDetection': '模式检测',
    'aa.patternDetectionDesc': '自动趋势和模式检测尚不可用。',
    'aa.updateTrainings': '更新训练',
    'aa.addKnowledgeTitle': '添加知识条目',
    'aa.addKnowledgeDesc': '向 AI 知识库添加新信息',
    'aa.titleRequired': '标题 *',
    'aa.knowledgeTitlePlaceholder': '知识条目标题',
    'aa.contentRequired': '内容 *',
    'aa.contentPlaceholder': '详细内容或解决方案',
    'aa.technical': '技术',
    'aa.billing': '账单',
    'aa.general': '一般',
    'aa.policy': '政策',
    'aa.priority': '优先级',
    'aa.low': '低',
    'aa.high': '高',
    'aa.tags': '标签',
    'aa.tagsPlaceholder': '用逗号分隔的标签',
    'aa.addToKnowledgeBase': '添加到知识库',
    'aa.importTitle': '导入知识库',
    'aa.importDesc': '从 CSV 或 JSON 文件导入知识条目',
    'aa.selectFile': '选择文件',
    'aa.supportedFormats': '支持的格式：CSV、JSON。最大文件大小：10MB',
    'aa.exportTitle': '导出知识库',
    'aa.exportDesc': '选择导出知识库的格式',
    'aa.csvFormat': 'CSV 格式',
    'aa.csvFormatDesc': '兼容电子表格',
    'aa.jsonFormat': 'JSON 格式',
    'aa.jsonFormatDesc': '结构化数据',
    'aa.reviewEscalatedTitle': '查看升级的案例',
    'aa.reviewEscalatedDesc': '查看并分析从 AI 升级到人工客服的案例',
    'aa.recentEscalatedCases': '最近升级的案例',
    'aa.noEscalatedCases': '尚无可查看的升级案例',
    'aa.escalatedWillAppear': '升级的对话将在即将发布的版本中显示在此处。',
    'aa.aiConfidenceLabel': 'AI 置信度：',
    'aa.escalationAnalysis': '升级分析',
    'aa.escalationAnalysisUnavailable': '升级分析尚不可用。',
    'aa.generateAnalysisReport': '生成分析报告',
    'aa.analysisReportUnavailable': '分析报告生成将在即将发布的版本中提供。',
    'aa.caseDetailsTitle': '案例详情 - #{id}',
    'aa.caseDetailsDesc': '有关升级案例的详细信息',
    'aa.caseId': '案例 ID',
    'aa.customer': '客户',
    'aa.unknownCustomer': '未知客户',
    'aa.escalated': '已升级',
    'aa.unknownTime': '未知时间',
    'aa.issueDescription': '问题描述',
    'aa.noIssueDescription': '没有可用的问题描述',
    'aa.aiConfidenceLevel': 'AI 置信度水平',
    'aa.escalationReason': '升级原因',
    'aa.escalationReasonText': 'AI 置信度水平低于阈值。这是需要人工专业知识的复杂技术问题。',
    'aa.provideFeedbackCaseTitle': '提供反馈 - 案例 #{id}',
    'aa.provideFeedbackDesc': '您的反馈有助于改进 AI 在类似案例中的表现',
    'aa.caseSummary': '案例摘要',
    'aa.feedbackCustomer': '客户：{name}',
    'aa.feedbackIssue': '问题：{issue}',
    'aa.yourFeedback': '您的反馈 *',
    'aa.feedbackPlaceholder': 'AI 如何才能更好地处理此案例？哪些额外的训练或知识会有帮助？',
    'aa.feedbackGuidelines': '反馈指南：',
    'aa.feedbackGuideline1': '描述 AI 遗漏或误解的内容',
    'aa.feedbackGuideline2': '建议知识库改进',
    'aa.feedbackGuideline3': '记录任何客户沟通问题',
    'aa.submitFeedback': '提交反馈',
    'aa.trainingFillScenario': '请填写场景和预期响应',
    'aa.trainingUnavailable': 'AI 模型训练将在即将发布的版本中提供。',
    'aa.fillTitleContent': '请填写标题和内容',
    'aa.knowledgeAdded': '已添加知识',
    'aa.knowledgeAddedDesc': '新条目已添加到知识库',
    'aa.addKnowledgeError': '无法添加知识库条目',
    'aa.importUnavailable': '知识库导入将在即将发布的版本中提供。',
    'aa.nothingToExport': '没有可导出的内容',
    'aa.nothingToExportDesc': '尚无可导出的知识库条目。',
    'aa.exportComplete': '导出完成',
    'aa.exportCompleteDesc': '知识库已导出为 {format}。',
    'aa.trainingUpdatesUnavailable': 'AI 训练更新将在即将发布的版本中提供。',
    'aa.feedbackRequired': '需要反馈',
    'aa.feedbackRequiredDesc': '请在提交前提供反馈。',
    'aa.feedbackSubmitted': '反馈已提交',
    'aa.feedbackSubmittedDesc': '案例 #{id} 的反馈已记录，将有助于改进 AI 性能。',
    'aa.saveConfigError': '无法保存 AI 配置',
    'aa.loadingRules': '正在加载自动解决规则...',
    'aa.autoResolutionMgmt': '自动解决管理',
    'aa.autoResolutionSubtitle': '配置智能响应规则和阈值',
    'aa.activeRulesBadge': '{count} 条活动规则',
    'aa.aiSettings': 'AI 设置',
    'aa.aiModel': 'AI 模型',
    'aa.advancedReasoning': '高级推理和智能客户支持',
    'aa.confidenceThresholdHint': '值越高 = 响应越谨慎（0.1-1.0）',
    'aa.advancedFeatures': '高级功能',
    'aa.activeLearningFromInteractions': '从客户互动中主动学习',
    'aa.activeRules': '活动规则',
    'aa.avgSuccessRate': '平均成功率',
    'aa.totalUsage': '总使用量',
    'aa.advancedAi': '高级 AI',
    'aa.tab.rules': '解决规则',
    'aa.tab.create': '创建规则',
    'aa.autoResolutionRulesCount': '自动解决规则（{count}）',
    'aa.keywordsLabel': '关键词：{keywords}',
    'aa.confidenceLabel': '置信度：{value}%',
    'aa.usedLabel': '已使用：{count} 次',
    'aa.successLabel': '成功：{value}%',
    'aa.createNewRule': '创建新的自动解决规则',
    'aa.ruleName': '规则名称 *',
    'aa.ruleNamePlaceholder': '输入规则名称',
    'aa.description': '描述',
    'aa.descriptionPlaceholder': '描述此规则处理的内容',
    'aa.keywordsCommaSeparated': '关键词（用逗号分隔）',
    'aa.keywordsPlaceholder': '密码, 重置, 忘记, 登录',
    'aa.autoResponse': '自动响应 *',
    'aa.autoResponsePlaceholder': '输入此规则的自动响应',
    'aa.enableRuleImmediately': '立即启用此规则',
    'aa.saveRule': '保存规则',
    'aa.responseLabel': '响应：',
    'aa.aiConfigSettings': 'AI 配置设置',
    'aa.aiEngine': 'AI 引擎',
    'aa.poweredBy': '由 {provider}（{model}）提供支持',
    'aa.resolutionAccuracy': '解决准确度',
    'aa.avgResponseTime': '平均响应时间',
    'aa.configureAiParams': '配置 AI 参数',
    'aa.editRuleTitle': '编辑自动解决规则',
    'aa.editRuleDesc': '修改自动解决规则设置',
    'aa.ruleIsEnabled': '规则已启用',
    'aa.updateRule': '更新规则',
    'aa.areYouSure': '您确定吗？',
    'aa.deleteConfirmPre': '此操作无法撤消。这将永久删除自动解决规则',
    'aa.deleteConfirmPost': '并移除所有关联数据。',
    'aa.deleteRule': '删除规则',
    'aa.fillRuleNameResponse': '请填写规则名称和响应',
    'aa.ruleCreated': '规则已创建',
    'aa.ruleCreatedDesc': '自动解决规则已成功创建',
    'aa.createRuleError': '无法创建自动解决规则',
    'aa.ruleUpdated': '规则已更新',
    'aa.ruleUpdatedDesc': '自动解决规则已成功更新',
    'aa.updateRuleError': '无法更新自动解决规则',
    'aa.ruleEnabledSuccess': '规则已成功启用',
    'aa.ruleDisabledSuccess': '规则已成功禁用',
    'aa.ruleDeleted': '规则已删除',
    'aa.ruleDeletedDesc': '自动解决规则已成功删除',
    'aa.deleteRuleError': '无法删除自动解决规则',
    'aa.loadRulesError': '无法加载自动解决规则',
    'aa.updateConfigError': '无法更新 AI 配置',
  },
  ar: {
    'aa.configTitle': 'إعداد وكيل الذكاء الاصطناعي',
    'aa.configDesc': 'قم بتكوين سلوك الذكاء الاصطناعي والحدود ومعلمات التشغيل',
    'aa.confidenceThreshold': 'حد الثقة',
    'aa.maxResponseTime': 'الحد الأقصى لوقت الاستجابة (ثوانٍ)',
    'aa.saveConfiguration': 'حفظ الإعداد',
    'aa.autoEscalation': 'التصعيد التلقائي للمشكلات المعقدة',
    'aa.missingInfo': 'معلومات ناقصة',
    'aa.aiConfigUpdated': 'تم تحديث إعداد الذكاء الاصطناعي',
    'aa.aiConfigSaved': 'تم حفظ إعدادات وكيل الذكاء الاصطناعي بنجاح',
    'aa.notAvailableYet': 'غير متاح بعد',
    'aa.viewDetails': 'عرض التفاصيل',
    'aa.provideFeedback': 'إرسال ملاحظات',
    'aa.import': 'استيراد',
    'aa.export': 'تصدير',
    'aa.active': 'نشط',
    'aa.disabled': 'معطّل',
    'aa.dashboardTitle': 'لوحة معلومات وكيل الذكاء الاصطناعي',
    'aa.dashboardSubtitle': 'نظام ذكي للحل من الخط الأول',
    'aa.managedBy': '| يُدار بواسطة {name}',
    'aa.configure': 'تكوين',
    'aa.confThreshold70': '70% - استجابات أكثر، دقة أقل',
    'aa.confThreshold80': '80% - نهج متوازن',
    'aa.confThreshold85': '85% - الإعداد الموصى به',
    'aa.confThreshold90': '90% - دقة عالية، استجابات أقل',
    'aa.confThreshold95': '95% - أقصى دقة',
    'aa.learningMode': 'وضع التعلّم',
    'aa.activeLearning': 'التعلّم النشط',
    'aa.passiveLearning': 'التعلّم السلبي',
    'aa.supervisedOnly': 'خاضع للإشراف فقط',
    'aa.features': 'الميزات',
    'aa.trainAI': 'تدريب الذكاء الاصطناعي',
    'aa.trainingSessionTitle': 'جلسة تدريب الذكاء الاصطناعي',
    'aa.trainingSessionDesc': 'أضف سيناريوهات تدريب جديدة لتحسين استجابات الذكاء الاصطناعي',
    'aa.customerScenario': 'سيناريو العميل *',
    'aa.scenarioPlaceholder': 'صِف مشكلة أو سؤالًا للعميل...',
    'aa.expectedResponse': 'الاستجابة المتوقعة من الذكاء الاصطناعي *',
    'aa.expectedResponsePlaceholder': 'الاستجابة المثالية التي يجب أن يقدمها الذكاء الاصطناعي...',
    'aa.difficultyLevel': 'مستوى الصعوبة',
    'aa.easy': 'سهل',
    'aa.medium': 'متوسط',
    'aa.hard': 'صعب',
    'aa.expert': 'خبير',
    'aa.category': 'الفئة',
    'aa.generalSupport': 'الدعم العام',
    'aa.technicalIssues': 'المشكلات التقنية',
    'aa.billingQuestions': 'أسئلة الفوترة',
    'aa.productInformation': 'معلومات المنتج',
    'aa.trainAIModel': 'تدريب نموذج الذكاء الاصطناعي',
    'aa.currentLoad': 'الحمل الحالي',
    'aa.activeAiConversations': 'محادثات الذكاء الاصطناعي النشطة',
    'aa.avgResponse': 'متوسط الاستجابة',
    'aa.responseTime': 'وقت الاستجابة',
    'aa.confidenceScore': 'درجة الثقة',
    'aa.avgConfidence': 'متوسط الثقة',
    'aa.queueLength': 'طول قائمة الانتظار',
    'aa.notAvailableYetLower': 'غير متاح بعد',
    'aa.tab.conversations': 'المحادثات النشطة',
    'aa.tab.analytics': 'تحليل الأداء',
    'aa.tab.knowledge': 'قاعدة المعرفة',
    'aa.tab.learning': 'التعلّم والملاحظات',
    'aa.realtimeConversations': 'محادثات في الوقت الفعلي ({count})',
    'aa.refreshConversations': 'تحديث المحادثات',
    'aa.loadingConversations': 'جارٍ تحميل المحادثات...',
    'aa.noActiveConversations': 'لا توجد محادثات ذكاء اصطناعي نشطة',
    'aa.aiHandlesAutomatically': 'سيتعامل الذكاء الاصطناعي مع المحادثات الجديدة تلقائيًا',
    'aa.customerInquiry': 'استفسار العميل',
    'aa.processing': 'جارٍ المعالجة...',
    'aa.justNow': 'الآن',
    'aa.convDetailUnavailable': 'ستتوفر عرض تفاصيل المحادثة في إصدار قادم.',
    'aa.escalate': 'تصعيد',
    'aa.escalationUnavailable': 'سيتوفر التصعيد داخل لوحة المعلومات في إصدار قادم.',
    'aa.aiStatus': 'حالة الذكاء الاصطناعي',
    'aa.telemetryUnavailable': 'القياس عن بُعد للنظام المباشر غير متاح بعد.',
    'aa.quickActions': 'إجراءات سريعة',
    'aa.addKnowledge': 'إضافة معرفة',
    'aa.viewAnalytics': 'عرض التحليلات',
    'aa.analyticsTitle': 'تحليل أداء الذكاء الاصطناعي',
    'aa.analyticsDesc': 'مقاييس ورؤى شاملة لأداء وكيل الذكاء الاصطناعي',
    'aa.autoResolutionRate': 'معدل الحل التلقائي',
    'aa.conversations': 'المحادثات',
    'aa.escalationRate': 'معدل التصعيد',
    'aa.performanceBreakdown': 'تفصيل الأداء',
    'aa.resolutionRate': 'معدل الحل',
    'aa.customerSatisfaction': 'رضا العملاء',
    'aa.responseTimeLabel': 'وقت الاستجابة',
    'aa.trainingLogs': 'سجلات التدريب',
    'aa.trainingLogsTitle': 'سجلات تدريب الذكاء الاصطناعي',
    'aa.trainingLogsDesc': 'جلسات التدريب الأخيرة وتحديثات النموذج',
    'aa.noTrainingLogs': 'لا توجد سجلات تدريب بعد',
    'aa.trainingHistoryUpcoming': 'سيظهر سجل التدريب هنا في إصدار قادم.',
    'aa.loadingAnalytics': 'جارٍ تحميل التحليلات',
    'aa.loadingAnalyticsDesc': 'جارٍ إنشاء تحليلات شاملة لأداء الذكاء الاصطناعي...',
    'aa.loadingTrainingLogs': 'جارٍ تحميل سجلات التدريب',
    'aa.loadingTrainingLogsDesc': 'جارٍ استرداد سجل تدريب الذكاء الاصطناعي وسجلات الأداء...',
    'aa.loadingCaseDetails': 'جارٍ تحميل تفاصيل الحالة',
    'aa.loadingCaseDetailsDesc': 'جارٍ استرداد تفاصيل الحالة رقم {id}',
    'aa.resolutionPerformance': 'أداء الحل',
    'aa.learningProgress': 'تقدم التعلّم',
    'aa.knowledgeBaseEntries': 'إدخالات قاعدة المعرفة',
    'aa.successfulResolutions': 'الحلول الناجحة',
    'aa.totalConversations': 'إجمالي المحادثات',
    'aa.knowledgeBaseEntriesCount': 'إدخالات قاعدة المعرفة ({count})',
    'aa.addEntry': 'إضافة إدخال',
    'aa.noKnowledgeEntries': 'لا توجد إدخالات في قاعدة المعرفة',
    'aa.addFirstEntry': 'أضف أول إدخال في قاعدة المعرفة لبدء تدريب الذكاء الاصطناعي',
    'aa.createFirstEntry': 'إنشاء أول إدخال',
    'aa.uses': '{count} استخدام',
    'aa.recentFeedback': 'الملاحظات الأخيرة',
    'aa.noFeedback': 'لا توجد ملاحظات بعد',
    'aa.feedbackWillAppear': 'ستظهر ملاحظات العملاء حول حلول الذكاء الاصطناعي هنا.',
    'aa.learningOpportunities': 'فرص التعلّم',
    'aa.improveResolutionRate': 'تحسين معدل الحل',
    'aa.improveResolutionDesc': 'راجع المحادثات المصعّدة للعثور على الثغرات التي يمكنك سدّها بإدخالات جديدة في قاعدة المعرفة.',
    'aa.reviewCases': 'مراجعة الحالات',
    'aa.patternDetection': 'اكتشاف الأنماط',
    'aa.patternDetectionDesc': 'اكتشاف الاتجاهات والأنماط التلقائي غير متاح بعد.',
    'aa.updateTrainings': 'تحديث التدريبات',
    'aa.addKnowledgeTitle': 'إضافة إدخال معرفة',
    'aa.addKnowledgeDesc': 'أضف معلومات جديدة إلى قاعدة معرفة الذكاء الاصطناعي',
    'aa.titleRequired': 'العنوان *',
    'aa.knowledgeTitlePlaceholder': 'عنوان إدخال المعرفة',
    'aa.contentRequired': 'المحتوى *',
    'aa.contentPlaceholder': 'محتوى مفصّل أو حل',
    'aa.technical': 'تقني',
    'aa.billing': 'الفوترة',
    'aa.general': 'عام',
    'aa.policy': 'سياسة',
    'aa.priority': 'الأولوية',
    'aa.low': 'منخفضة',
    'aa.high': 'عالية',
    'aa.tags': 'الوسوم',
    'aa.tagsPlaceholder': 'وسوم مفصولة بفواصل',
    'aa.addToKnowledgeBase': 'إضافة إلى قاعدة المعرفة',
    'aa.importTitle': 'استيراد قاعدة المعرفة',
    'aa.importDesc': 'استورد إدخالات المعرفة من ملف CSV أو JSON',
    'aa.selectFile': 'اختيار ملف',
    'aa.supportedFormats': 'الصيغ المدعومة: CSV وJSON. الحد الأقصى لحجم الملف: 10 ميغابايت',
    'aa.exportTitle': 'تصدير قاعدة المعرفة',
    'aa.exportDesc': 'اختر الصيغة لتصدير قاعدة المعرفة الخاصة بك',
    'aa.csvFormat': 'صيغة CSV',
    'aa.csvFormatDesc': 'متوافقة مع جداول البيانات',
    'aa.jsonFormat': 'صيغة JSON',
    'aa.jsonFormatDesc': 'بيانات منظّمة',
    'aa.reviewEscalatedTitle': 'مراجعة الحالات المصعّدة',
    'aa.reviewEscalatedDesc': 'راجع وحلّل الحالات التي تم تصعيدها من الذكاء الاصطناعي إلى الوكلاء البشريين',
    'aa.recentEscalatedCases': 'الحالات المصعّدة الأخيرة',
    'aa.noEscalatedCases': 'لا توجد حالات مصعّدة للمراجعة بعد',
    'aa.escalatedWillAppear': 'ستظهر المحادثات المصعّدة هنا في إصدار قادم.',
    'aa.aiConfidenceLabel': 'ثقة الذكاء الاصطناعي:',
    'aa.escalationAnalysis': 'تحليل التصعيد',
    'aa.escalationAnalysisUnavailable': 'تحليل التصعيد غير متاح بعد.',
    'aa.generateAnalysisReport': 'إنشاء تقرير تحليل',
    'aa.analysisReportUnavailable': 'سيتوفر إنشاء تقارير التحليل في إصدار قادم.',
    'aa.caseDetailsTitle': 'تفاصيل الحالة - رقم {id}',
    'aa.caseDetailsDesc': 'معلومات مفصّلة عن الحالة المصعّدة',
    'aa.caseId': 'معرّف الحالة',
    'aa.customer': 'العميل',
    'aa.unknownCustomer': 'عميل غير معروف',
    'aa.escalated': 'مُصعّد',
    'aa.unknownTime': 'وقت غير معروف',
    'aa.issueDescription': 'وصف المشكلة',
    'aa.noIssueDescription': 'لا يوجد وصف متاح للمشكلة',
    'aa.aiConfidenceLevel': 'مستوى ثقة الذكاء الاصطناعي',
    'aa.escalationReason': 'سبب التصعيد',
    'aa.escalationReasonText': 'كان مستوى ثقة الذكاء الاصطناعي أقل من الحد. مشكلة تقنية معقدة تتطلب خبرة بشرية.',
    'aa.provideFeedbackCaseTitle': 'إرسال ملاحظات - الحالة رقم {id}',
    'aa.provideFeedbackDesc': 'تساعد ملاحظاتك في تحسين أداء الذكاء الاصطناعي في الحالات المماثلة',
    'aa.caseSummary': 'ملخص الحالة',
    'aa.feedbackCustomer': 'العميل: {name}',
    'aa.feedbackIssue': 'المشكلة: {issue}',
    'aa.yourFeedback': 'ملاحظاتك *',
    'aa.feedbackPlaceholder': 'كيف كان بإمكان الذكاء الاصطناعي التعامل مع هذه الحالة بشكل أفضل؟ ما التدريب أو المعرفة الإضافية التي ستساعد؟',
    'aa.feedbackGuidelines': 'إرشادات الملاحظات:',
    'aa.feedbackGuideline1': 'صِف ما أغفله الذكاء الاصطناعي أو أساء فهمه',
    'aa.feedbackGuideline2': 'اقترح تحسينات لقاعدة المعرفة',
    'aa.feedbackGuideline3': 'دوّن أي مشكلات في التواصل مع العميل',
    'aa.submitFeedback': 'إرسال الملاحظات',
    'aa.trainingFillScenario': 'يرجى ملء السيناريو والاستجابة المتوقعة',
    'aa.trainingUnavailable': 'سيتوفر تدريب نموذج الذكاء الاصطناعي في إصدار قادم.',
    'aa.fillTitleContent': 'يرجى ملء العنوان والمحتوى',
    'aa.knowledgeAdded': 'تمت إضافة المعرفة',
    'aa.knowledgeAddedDesc': 'تمت إضافة إدخال جديد إلى قاعدة المعرفة',
    'aa.addKnowledgeError': 'فشل في إضافة إدخال قاعدة المعرفة',
    'aa.importUnavailable': 'سيتوفر استيراد قاعدة المعرفة في إصدار قادم.',
    'aa.nothingToExport': 'لا يوجد شيء للتصدير',
    'aa.nothingToExportDesc': 'لا توجد بعد إدخالات في قاعدة المعرفة للتصدير.',
    'aa.exportComplete': 'اكتمل التصدير',
    'aa.exportCompleteDesc': 'تم تصدير قاعدة المعرفة بصيغة {format}.',
    'aa.trainingUpdatesUnavailable': 'ستتوفر تحديثات تدريب الذكاء الاصطناعي في إصدار قادم.',
    'aa.feedbackRequired': 'الملاحظات مطلوبة',
    'aa.feedbackRequiredDesc': 'يرجى تقديم ملاحظات قبل الإرسال.',
    'aa.feedbackSubmitted': 'تم إرسال الملاحظات',
    'aa.feedbackSubmittedDesc': 'تم تسجيل ملاحظات الحالة رقم {id} وستساعد في تحسين أداء الذكاء الاصطناعي.',
    'aa.saveConfigError': 'فشل في حفظ إعداد الذكاء الاصطناعي',
    'aa.loadingRules': 'جارٍ تحميل قواعد الحل التلقائي...',
    'aa.autoResolutionMgmt': 'إدارة الحل التلقائي',
    'aa.autoResolutionSubtitle': 'قم بتكوين قواعد الاستجابة الذكية والحدود',
    'aa.activeRulesBadge': '{count} قاعدة نشطة',
    'aa.aiSettings': 'إعدادات الذكاء الاصطناعي',
    'aa.aiModel': 'نموذج الذكاء الاصطناعي',
    'aa.advancedReasoning': 'استدلال متقدم ودعم عملاء ذكي',
    'aa.confidenceThresholdHint': 'القيم الأعلى = استجابات أكثر حذرًا (0.1-1.0)',
    'aa.advancedFeatures': 'ميزات متقدمة',
    'aa.activeLearningFromInteractions': 'التعلّم النشط من تفاعلات العملاء',
    'aa.activeRules': 'القواعد النشطة',
    'aa.avgSuccessRate': 'متوسط معدل النجاح',
    'aa.totalUsage': 'إجمالي الاستخدام',
    'aa.advancedAi': 'ذكاء اصطناعي متقدم',
    'aa.tab.rules': 'قواعد الحل',
    'aa.tab.create': 'إنشاء قاعدة',
    'aa.autoResolutionRulesCount': 'قواعد الحل التلقائي ({count})',
    'aa.keywordsLabel': 'الكلمات المفتاحية: {keywords}',
    'aa.confidenceLabel': 'الثقة: {value}%',
    'aa.usedLabel': 'استُخدمت: {count} مرة',
    'aa.successLabel': 'النجاح: {value}%',
    'aa.createNewRule': 'إنشاء قاعدة حل تلقائي جديدة',
    'aa.ruleName': 'اسم القاعدة *',
    'aa.ruleNamePlaceholder': 'أدخل اسم القاعدة',
    'aa.description': 'الوصف',
    'aa.descriptionPlaceholder': 'صِف ما تتعامل معه هذه القاعدة',
    'aa.keywordsCommaSeparated': 'الكلمات المفتاحية (مفصولة بفواصل)',
    'aa.keywordsPlaceholder': 'كلمة المرور، إعادة تعيين، نسيت، تسجيل الدخول',
    'aa.autoResponse': 'الاستجابة التلقائية *',
    'aa.autoResponsePlaceholder': 'أدخل الاستجابة التلقائية لهذه القاعدة',
    'aa.enableRuleImmediately': 'تفعيل هذه القاعدة فورًا',
    'aa.saveRule': 'حفظ القاعدة',
    'aa.responseLabel': 'الاستجابة:',
    'aa.aiConfigSettings': 'إعدادات تكوين الذكاء الاصطناعي',
    'aa.aiEngine': 'محرك الذكاء الاصطناعي',
    'aa.poweredBy': 'مدعوم بواسطة {provider} ({model})',
    'aa.resolutionAccuracy': 'دقة الحل',
    'aa.avgResponseTime': 'متوسط وقت الاستجابة',
    'aa.configureAiParams': 'تكوين معلمات الذكاء الاصطناعي',
    'aa.editRuleTitle': 'تعديل قاعدة الحل التلقائي',
    'aa.editRuleDesc': 'عدّل إعدادات قاعدة الحل التلقائي',
    'aa.ruleIsEnabled': 'القاعدة مفعّلة',
    'aa.updateRule': 'تحديث القاعدة',
    'aa.areYouSure': 'هل أنت متأكد؟',
    'aa.deleteConfirmPre': 'لا يمكن التراجع عن هذا الإجراء. سيؤدي هذا إلى حذف قاعدة الحل التلقائي نهائيًا',
    'aa.deleteConfirmPost': 'وإزالة جميع البيانات المرتبطة.',
    'aa.deleteRule': 'حذف القاعدة',
    'aa.fillRuleNameResponse': 'يرجى ملء اسم القاعدة والاستجابة',
    'aa.ruleCreated': 'تم إنشاء القاعدة',
    'aa.ruleCreatedDesc': 'تم إنشاء قاعدة الحل التلقائي بنجاح',
    'aa.createRuleError': 'فشل في إنشاء قاعدة الحل التلقائي',
    'aa.ruleUpdated': 'تم تحديث القاعدة',
    'aa.ruleUpdatedDesc': 'تم تحديث قاعدة الحل التلقائي بنجاح',
    'aa.updateRuleError': 'فشل في تحديث قاعدة الحل التلقائي',
    'aa.ruleEnabledSuccess': 'تم تفعيل القاعدة بنجاح',
    'aa.ruleDisabledSuccess': 'تم تعطيل القاعدة بنجاح',
    'aa.ruleDeleted': 'تم حذف القاعدة',
    'aa.ruleDeletedDesc': 'تم حذف قاعدة الحل التلقائي بنجاح',
    'aa.deleteRuleError': 'فشل في حذف قاعدة الحل التلقائي',
    'aa.loadRulesError': 'فشل في تحميل قواعد الحل التلقائي',
    'aa.updateConfigError': 'فشل في تحديث إعداد الذكاء الاصطناعي',
  },
};

export default aiAgentTranslations;
